// src/modulos/Parametros/ProductsPage.js

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { db } from '../../firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './Registers.module.css';

// React Icons
import {
    FiPlus,
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiSearch,
    FiPackage,
    FiUser,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiFileText,
    FiPrinter,
    FiArrowLeft,
    FiArrowRight,
    FiDownload,
    FiUpload
} from 'react-icons/fi';
import {
    FaBox,
    FaCogs,
    FaBalanceScale,
    FaWeight,
    FaRuler,
    FaClock,
    FaIndustry,
    FaShoppingCart,
    FaDollarSign,
    FaPercent,
    FaCheckCircle,
    FaExclamationTriangle,
    FaInfoCircle,
    FaTags,
    FaLayerGroup,
    FaMicroscope
} from 'react-icons/fa';
import { IoCheckmarkOutline } from 'react-icons/io5';
import { SlChemistry } from 'react-icons/sl';
import { GrDocumentTransfer } from 'react-icons/gr';
import { MdCategory, MdBusiness, MdDescription, MdLocationOn } from 'react-icons/md';

// Common Components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
    Toast,
    TagInput,
} from '../../components/common';

import useToast from '../../hooks/useToast';
import usePagination from '../../hooks/usePagination';

// 1. Constantes e Definicoes da Colecao
const PRODUCTS_COLLECTION = 'products';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const CRITERIA_COLLECTION = 'parameters/data/criterio-qualidade';
const PRODUCT_TYPE_COLLECTION_PATH = 'parameters/types/tipo-produto';
const CATEGORY_COLLECTION_PATH = 'parameters/data/product-categories';
const PREPARATION_SETUP_COLLECTION = 'parameters/production/setups-preparo';
const NCM_COLLECTION = 'parameters/data/ncm';
const CFOP_COLLECTION = 'parameters/data/cfop';
const BOX_TYPE_COLLECTION_PATH = 'parameters/types/tipo-caixa';

const ITEMS_PER_PAGE = 10;

const SITUATION_OPTIONS = ['Ativo', 'Inativo'];
const DEFAULT_SITUATION = SITUATION_OPTIONS[0];

const ANALISIS_CONTROL_OPTIONS = [
    { value: 'Processo', label: 'Processo' },
    { value: 'Produto acabado', label: 'Produto acabado' },
    { value: 'Ambos', label: 'Ambos' },
];

// Opções para as fases de produção
const PRODUCTION_PHASES_OPTIONS = [
    { value: 'Pesagem', label: 'Pesagem' },
    { value: 'Producao', label: 'Produção' },
    { value: 'ControleQualidadeProcesso', label: 'Controle Qualidade Processo' },
    { value: 'LiberacaoProducao', label: 'Liberação da Produção' },
    { value: 'ConferenciaEmbalagem', label: 'Conferencia Embalagem' },
    { value: 'Envase', label: 'Envase' },
    { value: 'Rotulagem', label: 'Rotulagem' },
];

const TABS = {
    FISCAL: 'Fiscal',
    ESPECIFICACAO_TECNICA: 'Especificacao Tecnica',
    TECNICO: 'Tecnico',
    MATERIAIS_E_PROC: 'Materiais e Proc.',
};

// Opções para a aba Fiscal
const ORIGEM_OPERACAO_COMERCIAL_OPTIONS = [
    'Fabricação Própria',
    'Compra para Revenda',
    'Não Controlado'
];

const ORIGEM_MERCADORIA_ICMS_OPTIONS = [
    { code: '0', label: '0 - Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8' },
    { code: '1', label: '1 - Estrangeira - Importação direta, exceto a indicada no código 6' },
    { code: '2', label: '2 - Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7' },
    { code: '3', label: '3 - Nacional, mercadoria ou bem com Conteúdo de Importação superior a 40% e inferior ou igual a 70%' },
    { code: '4', label: '4 - Nacional, cuja produção tenha sido feita em conformidade com os processos produtivos básicos' },
    { code: '5', label: '5 - Nacional, mercadoria ou bem com Conteúdo de Importação inferior ou igual a 40%' },
    { code: '6', label: '6 - Estrangeira - Importação direta, sem similar nacional, constante em lista CAMEX e gás natural' },
    { code: '7', label: '7 - Estrangeira - Adquirida no mercado interno, s/ similar nacional, const. lista CAMEX e gás natural' },
    { code: '8', label: '8 - Nacional, mercadoria ou bem com Conteúdo de Importação superior a 70%' }
];

// Memoized collection paths to prevent re-renders
const COLLECTION_PATHS = {
    PRODUCTS: PRODUCTS_COLLECTION,
    CATEGORIES: CATEGORY_COLLECTION_PATH,
    PRODUCT_TYPES: PRODUCT_TYPE_COLLECTION_PATH,
};

// Skeleton Row Component
const SkeletonRow = () => (
    <tr className={styles.skeletonRow}>
        <td><div className={styles.skeletonCell} style={{ width: '80px' }}></div></td>
        <td><div className={styles.skeletonCell} style={{ width: '150px' }}></div></td>
        <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
        <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
        <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
        <td><div className={styles.skeletonCell} style={{ width: '180px' }}></div></td>
    </tr>
);

const ProductsPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('produtos');
    const { toasts, addToast, removeToast } = useToast();

    // --- Estados Principais ---
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(TABS.MATERIAIS_E_PROC);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [formLoading, setFormLoading] = useState(false);

    // Reference to store original data for dirty checking
    const originalItemDataRef = useRef(null);

    // --- Estados para Busca, Filtros Principais e Paginacao ---
    const [mainSearchTerm, setMainSearchTerm] = useState('');
    const [mainFilterProductTypeId, setMainFilterProductTypeId] = useState('');
    const [mainFilterCategoryId, setMainFilterCategoryId] = useState('');

    // --- Estado para versao dos produtos (para forcar refresh) ---
    const [productsVersion, setProductsVersion] = useState(0);

    // --- Estados dos Campos da Secao 1 (Dados Gerais) ---
    const [sigla, setSigla] = useState('');
    const [code, setCode] = useState('');
    const [recommendedCode, setRecommendedCode] = useState('');
    const [commercialName, setCommercialName] = useState('');
    const [situation, setSituation] = useState(DEFAULT_SITUATION);
    const [barcode, setBarcode] = useState('');
    const [netWeight, setNetWeight] = useState('');
    const [grossWeight, setGrossWeight] = useState('');
    const [density, setDensity] = useState('');
    const [lossIndex, setLossIndex] = useState('');

    // --- Estados para Classificacao (Tipo e Categoria) ---
    const [selectedProductTypeIds, setSelectedProductTypeIds] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

    // --- Estados da Aba Tecnico ---
    const [gramatura, setGramatura] = useState('');
    const [gramaturaUnit, setGramaturaUnit] = useState('g');
    const [shelfLifeMonths, setShelfLifeMonths] = useState('');
    const [selectedProductionPhases, setSelectedProductionPhases] = useState([]);

    // --- NOVO: Múltiplos tipos de caixa com suas respectivas unidades por caixa ---
    const [boxTypeConfigs, setBoxTypeConfigs] = useState([]); // Array of { boxTypeId, unitsPerBox }

    // --- Estados da Aba Materiais e Proc. ---
    const [rawMaterialsList, setRawMaterialsList] = useState([]);
    const [qualityCriteria, setQualityCriteria] = useState([]);
    const [preparationSteps, setPreparationSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState('');
    const [markup, setMarkup] = useState('1.00');
    const [selectedSetup, setSelectedSetup] = useState('');

    // --- Estados para Importacao de MPs ---
    const [showImportMPModal, setShowImportMPModal] = useState(false);
    const [importSearchTerm, setImportSearchTerm] = useState('');

    // --- Estados da Aba Fiscal ---
    const [origemOperacaoComercial, setOrigemOperacaoComercial] = useState('Fabricação Própria');
    const [origemMercadoriaIcms, setOrigemMercadoriaIcms] = useState('0');
    const [selectedNcmId, setSelectedNcmId] = useState('');
    const [excecaoFiscalIpi, setExcecaoFiscalIpi] = useState(false);
    const [excecaoFiscalIva, setExcecaoFiscalIva] = useState(false);
    const [excecaoFiscalPisCofins, setExcecaoFiscalPisCofins] = useState(false);
    const [valorPautaRs, setValorPautaRs] = useState('');
    const [classificacaoTributariaRt, setClassificacaoTributariaRt] = useState('');
    const [cfopSaidaEstadual, setCfopSaidaEstadual] = useState('');
    const [cfopSaidaEstadualComSt, setCfopSaidaEstadualComSt] = useState('');
    const [cfopIndustrializacaoEstadual, setCfopIndustrializacaoEstadual] = useState('');
    const [cfopSaidaBrindeBonificacaoDoacaoEstadual, setCfopSaidaBrindeBonificacaoDoacaoEstadual] = useState('');
    const [cfopSaidaBrindeBonificacaoDoacaoEstadualComSt, setCfopSaidaBrindeBonificacaoDoacaoEstadualComSt] = useState('');
    const [cfopSaidaNaoContribuinteInterestadual, setCfopSaidaNaoContribuinteInterestadual] = useState('');
    const [cfopBonificacaoExportacao, setCfopBonificacaoExportacao] = useState('');
    const [baseCalculoSt, setBaseCalculoSt] = useState('');
    const [ipiSaida, setIpiSaida] = useState('');
    const [cfopSaidaInterestadual, setCfopSaidaInterestadual] = useState('');
    const [cfopSaidaBrindeBonificacaoDoacaoInterestadual, setCfopSaidaBrindeBonificacaoDoacaoInterestadual] = useState('');
    const [cfopSaidaNaoContribuinteInterestadualOutro, setCfopSaidaNaoContribuinteInterestadualOutro] = useState('');
    const [cfopBonificacaoExportacaoOutro, setCfopBonificacaoExportacaoOutro] = useState('');
    const [cfopExportacao, setCfopExportacao] = useState('');

    // --- 7. Hooks de Dados Principais ---
    const {
        data: products,
        loading: mainLoading,
        addDocument: createDocument,
        updateDocument,
        deleteDocument,
        refreshData: refreshProducts
    } = useFirestore(COLLECTION_PATHS.PRODUCTS);

    const { data: categories, loading: categoriesLoading } = useFirestore(COLLECTION_PATHS.CATEGORIES);
    const { data: productTypes, loading: productTypesLoading } = useFirestore(COLLECTION_PATHS.PRODUCT_TYPES);

    // --- DADOS AUXILIARES ---
    const [allRawMaterials, setAllRawMaterials] = useState([]);
    const [allQualityCriteria, setAllQualityCriteria] = useState([]);
    const [allProductTypes, setAllProductTypes] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [preparationSetups, setPreparationSetups] = useState([]);
    const [allNcms, setAllNcms] = useState([]);
    const [allCfops, setAllCfops] = useState([]);
    const [boxTypes, setBoxTypes] = useState([]);
    const [auxiliaryDataLoading, setAuxiliaryDataLoading] = useState(true);

    // Filtros internos do Modal
    const [filterProductTypeId, setFilterProductTypeId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');

    // --- Função auxiliar para obter nome do tipo de produto ---
    const getBoxProductTypeName = useCallback((productTypeId) => {
        const productType = allProductTypes?.find(pt => pt.id === productTypeId);
        return productType ? productType.name : '';
    }, [allProductTypes]);

    // ============================================================================
    // getLatestPrice - Now prioritizes custoCompra as the main price
    // ============================================================================
    const getLatestPrice = useCallback((mpId, fallbackValue) => {
        const latestMP = allRawMaterials.find(item => item.id === mpId);

        if (latestMP && latestMP.custoCompra !== undefined && latestMP.custoCompra !== null) {
            const price = parseFloat(latestMP.custoCompra);
            if (!isNaN(price) && price > 0) {
                return price;
            }
        }

        if (latestMP && latestMP.suppliers && latestMP.suppliers[0]) {
            const price = parseFloat(latestMP.suppliers[0].valor);
            if (!isNaN(price)) {
                return price || 0;
            }
        }

        const latestProduct = products.find(p => p.id === mpId);
        if (latestProduct) {
            return parseFloat(latestProduct.totalFormulationCost) || 0;
        }

        return parseFloat(fallbackValue) || 0;
    }, [allRawMaterials, products]);

    const getGroupedBoxTypes = useMemo(() => {
        const activeBoxTypes = boxTypes.filter(box => box.situation !== 'Inativo');
        const groups = {};
        activeBoxTypes.forEach(box => {
            const productTypeName = getBoxProductTypeName(box.productTypeId);
            const groupKey = productTypeName || 'Sem Tipo';
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(box);
        });
        return groups;
    }, [boxTypes, getBoxProductTypeName]);

    // --- FUNCAO DE FETCH DE DADOS AUXILIARES ---
    const fetchAuxiliaryData = useCallback(async () => {
        setAuxiliaryDataLoading(true);
        try {
            const mpCollectionRef = collection(db, RAW_MATERIALS_COLLECTION);
            const mpSnapshot = await getDocs(mpCollectionRef);
            let fetchedRawMaterials = mpSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const criteriaCollectionRef = collection(db, CRITERIA_COLLECTION);
            const criteriaSnapshot = await getDocs(criteriaCollectionRef);
            let fetchedCriteria = criteriaSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const typesCollectionRef = collection(db, PRODUCT_TYPE_COLLECTION_PATH);
            const typesSnapshot = await getDocs(typesCollectionRef);
            let fetchedTypes = typesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedTypes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            const categoriesCollectionRef = collection(db, CATEGORY_COLLECTION_PATH);
            const categoriesSnapshot = await getDocs(categoriesCollectionRef);
            let fetchedCategories = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedCategories.sort((a, b) => (a.order || 0) - (b.order || 0));

            const setupsCollectionRef = collection(db, PREPARATION_SETUP_COLLECTION);
            const setupsSnapshot = await getDocs(setupsCollectionRef);
            let fetchedSetups = setupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedSetups.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            const ncmCollectionRef = collection(db, NCM_COLLECTION);
            const ncmSnapshot = await getDocs(ncmCollectionRef);
            let fetchedNcms = ncmSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedNcms.sort((a, b) => (a.ncmCode || '').localeCompare(b.ncmCode || '', undefined, { numeric: true }));

            const cfopCollectionRef = collection(db, CFOP_COLLECTION);
            const cfopSnapshot = await getDocs(cfopCollectionRef);
            let fetchedCfops = cfopSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedCfops.sort((a, b) => (a.codigoOperacao || '').localeCompare(b.codigoOperacao || '', undefined, { numeric: true }));

            const boxTypesCollectionRef = collection(db, BOX_TYPE_COLLECTION_PATH);
            const boxTypesSnapshot = await getDocs(boxTypesCollectionRef);
            let fetchedBoxTypes = boxTypesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedBoxTypes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            setAllRawMaterials(fetchedRawMaterials);
            setAllQualityCriteria(fetchedCriteria);
            setAllProductTypes(fetchedTypes);
            setAllCategories(fetchedCategories);
            setPreparationSetups(fetchedSetups);
            setAllNcms(fetchedNcms);
            setAllCfops(fetchedCfops);
            setBoxTypes(fetchedBoxTypes);

        } catch (error) {
            console.error("Erro ao buscar dados auxiliares.", error);
            addToast(t('pages.products.auxiliaryDataError'), 'error');
        } finally {
            setAuxiliaryDataLoading(false);
        }
    }, [t, addToast]);

    useEffect(() => {
        fetchAuxiliaryData();
    }, [fetchAuxiliaryData]);

    // --- Handlers para Classificacao (Tags) ---
    const handleAddProductType = useCallback((option) => {
        if (option && !selectedProductTypeIds.includes(option.id)) {
            setSelectedProductTypeIds(prev => [...prev, option.id]);
        }
    }, [selectedProductTypeIds]);

    const handleRemoveProductType = useCallback((option) => {
        setSelectedProductTypeIds(prev => prev.filter(id => id !== option.id));
    }, []);

    const handleAddCategory = useCallback((option) => {
        if (option && !selectedCategoryIds.includes(option.id)) {
            setSelectedCategoryIds(prev => [...prev, option.id]);
        }
    }, [selectedCategoryIds]);

    const handleRemoveCategory = useCallback((option) => {
        setSelectedCategoryIds(prev => prev.filter(id => id !== option.id));
    }, []);

    // --- Handlers para Configurações de Caixa ---
    const handleAddBoxType = useCallback((option) => {
        if (!option) return;

        // Check if this box type is already added
        if (boxTypeConfigs.some(config => config.boxTypeId === option.id)) {
            addToast(t('pages.products.technical.boxTypeAlreadyAdded'), 'warning');
            return;
        }

        // Get the box details
        const box = boxTypes.find(b => b.id === option.id);
        if (!box) return;

        // Add with default unitsPerBox = 1
        setBoxTypeConfigs(prev => [...prev, {
            boxTypeId: option.id,
            unitsPerBox: 1,
            boxName: box.name,
            boxDetails: box
        }]);
    }, [boxTypeConfigs, boxTypes, addToast, t]);

    const handleRemoveBoxType = useCallback((boxTypeId) => {
        setBoxTypeConfigs(prev => prev.filter(config => config.boxTypeId !== boxTypeId));
    }, []);

    const handleUpdateUnitsPerBox = useCallback((boxTypeId, units) => {
        setBoxTypeConfigs(prev => prev.map(config =>
            config.boxTypeId === boxTypeId
                ? { ...config, unitsPerBox: parseInt(units) || 0 }
                : config
        ));
    }, []);

    const getParamName = (id, paramsList) => {
        if (!id || !paramsList) return 'N/A';
        const item = paramsList.find(p => p.id === id);
        return item ? (item.name || item.analysis || 'N/A') : 'N/A';
    };

    // --- Handlers para Fases de Producao (Checkboxes) ---
    const handleProductionPhaseChange = (phaseValue, isChecked) => {
        if (isChecked) {
            setSelectedProductionPhases(prev => [...prev, phaseValue]);
        } else {
            setSelectedProductionPhases(prev => prev.filter(p => p !== phaseValue));
        }
    };

    // --- FUNCAO: Gerar codigo sequencial ---
    const generateNextSequentialCode = useCallback((siglaValue, existingProducts) => {
        if (!siglaValue) return '';
        const prefix = siglaValue.toUpperCase().trim();
        if (prefix.length < 2) return '';

        const productsWithPrefix = existingProducts.filter(item =>
            item.code && item.code.startsWith(prefix)
        );

        if (productsWithPrefix.length === 0) {
            return `${prefix}001`;
        }

        const numbers = [];
        for (const product of productsWithPrefix) {
            const numberPart = product.code.slice(prefix.length);
            const num = parseInt(numberPart, 10);
            if (!isNaN(num) && numberPart.length > 0) {
                numbers.push(num);
            }
        }

        if (numbers.length === 0) {
            return `${prefix}001`;
        }

        const sortedNumbers = numbers.sort((a, b) => a - b);
        let nextNumber = 1;
        for (let i = 0; i < sortedNumbers.length; i++) {
            if (sortedNumbers[i] > nextNumber) {
                break;
            }
            if (sortedNumbers[i] === nextNumber) {
                nextNumber++;
            }
        }

        return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
    }, []);

    // --- Funcao para validar se um codigo ja existe ---
    const isCodeUnique = useCallback((codeToCheck, excludeId = null) => {
        if (!products) return true;
        return !products.some(product =>
            product.code === codeToCheck &&
            (excludeId === null || product.id !== excludeId)
        );
    }, [products]);

    // --- Função para validar código de barras (apenas números) ---
    const validateBarcode = useCallback((barcodeValue) => {
        if (!barcodeValue) return true;
        return /^\d+$/.test(barcodeValue);
    }, []);

    // --- Atualizar codigo baseado na sigla ---
    useEffect(() => {
        if (!editingItem && sigla && products) {
            const recommendation = generateNextSequentialCode(sigla, products);
            setRecommendedCode(recommendation);
            if (!code || code === recommendedCode) {
                setCode(recommendation);
            }
        } else if (!sigla) {
            setCode('');
            setRecommendedCode('');
        }
    }, [sigla, products, editingItem, recommendedCode, code, generateNextSequentialCode]);

    // --- Handler para mudancas manuais no codigo ---
    const handleCodeChange = (e) => {
        const newCode = e.target.value.toUpperCase().trim();

        if (!sigla) {
            setFormStatus({ message: t('pages.products.form.codeRequiresAcronym'), type: 'error' });
            return;
        }

        if (!newCode.startsWith(sigla.toUpperCase())) {
            setFormStatus({
                message: t('pages.products.form.codeMustStartWithAcronym', { acronym: sigla.toUpperCase() }),
                type: 'error'
            });
            return;
        }

        if (newCode.length <= sigla.length) {
            setFormStatus({ message: t('pages.products.form.codeMustHaveNumbers'), type: 'error' });
            return;
        }

        const numberPart = newCode.slice(sigla.length);
        if (numberPart.length === 0 || !/^\d+$/.test(numberPart)) {
            setFormStatus({ message: t('pages.products.form.codeNumbersOnly'), type: 'error' });
            return;
        }

        setFormStatus({ message: '', type: '' });
        setCode(newCode);
    };

    // --- Handler para quando a sigla muda ---
    const handleSiglaChange = (e) => {
        const newSigla = e.target.value.toUpperCase().trim();
        setSigla(newSigla);
        if (!editingItem) {
            setCode('');
            setRecommendedCode('');
            setFormStatus({ message: '', type: '' });
        }
    };

    // --- Geracao de Codigo Recomendado ---
    const getCodeRecommendation = useCallback(() => {
        if (!sigla || !products) return '';
        if (editingItem) return '';
        return generateNextSequentialCode(sigla, products);
    }, [sigla, products, editingItem, generateNextSequentialCode]);

    // --- Funcao para verificar se os dados do formulario foram alterados ---
    const isFormDirty = useCallback(() => {
        if (!editingItem) return true;
        if (!originalItemDataRef.current) return true;

        const original = originalItemDataRef.current;

        const basicFieldsChanged =
            original.sigla !== sigla ||
            original.code !== code ||
            original.commercialName !== commercialName ||
            original.situation !== situation ||
            original.barcode !== barcode ||
            parseFloat(original.netWeight || 0) !== parseFloat(netWeight || 0) ||
            parseFloat(original.grossWeight || 0) !== parseFloat(grossWeight || 0) ||
            parseFloat(original.density || 0) !== parseFloat(density || 0) ||
            parseFloat(original.lossIndex || 0) !== parseFloat(lossIndex || 0);

        if (basicFieldsChanged) return true;

        const productTypesChanged = JSON.stringify(original.productTypeIds || []) !== JSON.stringify(selectedProductTypeIds);
        if (productTypesChanged) return true;

        const categoriesChanged = JSON.stringify(original.categoryIds || []) !== JSON.stringify(selectedCategoryIds);
        if (categoriesChanged) return true;

        const technicalChanged =
            parseFloat(original.gramatura || 0) !== parseFloat(gramatura || 0) ||
            original.gramaturaUnit !== gramaturaUnit ||
            parseFloat(original.shelfLifeMonths || 0) !== parseFloat(shelfLifeMonths || 0) ||
            JSON.stringify(original.productionPhases || []) !== JSON.stringify(selectedProductionPhases) ||
            JSON.stringify(original.boxTypeConfigs || []) !== JSON.stringify(boxTypeConfigs);

        if (technicalChanged) return true;

        const rawMaterialsChanged = JSON.stringify(original.rawMaterials || []) !== JSON.stringify(rawMaterialsList);
        if (rawMaterialsChanged) return true;

        const criteriaChanged = JSON.stringify(original.qualityCriteria || []) !== JSON.stringify(qualityCriteria);
        if (criteriaChanged) return true;

        const stepsChanged = JSON.stringify(original.preparationSteps || []) !== JSON.stringify(preparationSteps);
        if (stepsChanged) return true;

        const markupChanged = parseFloat(original.markup || 1) !== parseFloat(markup || 1);
        if (markupChanged) return true;

        const fiscalChanged =
            JSON.stringify(original.fiscal || {}) !== JSON.stringify({
                origemOperacaoComercial,
                origemMercadoriaIcms,
                ncmId: selectedNcmId,
                excecaoFiscalIpi,
                excecaoFiscalIva,
                excecaoFiscalPisCofins,
                valorPautaRs: parseFloat(valorPautaRs) || 0,
                classificacaoTributariaRt,
                cfopSaidaEstadual,
                cfopSaidaEstadualComSt,
                cfopIndustrializacaoEstadual,
                cfopSaidaBrindeBonificacaoDoacaoEstadual,
                cfopSaidaBrindeBonificacaoDoacaoEstadualComSt,
                cfopSaidaNaoContribuinteInterestadual,
                cfopBonificacaoExportacao,
                baseCalculoSt: parseFloat(baseCalculoSt) || 0,
                ipiSaida: parseFloat(ipiSaida) || 0,
                cfopSaidaInterestadual,
                cfopSaidaBrindeBonificacaoDoacaoInterestadual,
                cfopSaidaNaoContribuinteInterestadualOutro,
                cfopBonificacaoExportacaoOutro,
            });

        if (fiscalChanged) return true;

        return false;
    }, [editingItem, sigla, code, commercialName, situation, barcode, netWeight, grossWeight, density, lossIndex, gramatura, gramaturaUnit, shelfLifeMonths, selectedProductionPhases, boxTypeConfigs, selectedProductTypeIds, selectedCategoryIds, rawMaterialsList, qualityCriteria, preparationSteps, markup, origemOperacaoComercial, origemMercadoriaIcms, selectedNcmId, excecaoFiscalIpi, excecaoFiscalIva, excecaoFiscalPisCofins, valorPautaRs, classificacaoTributariaRt, cfopSaidaEstadual, cfopSaidaEstadualComSt, cfopIndustrializacaoEstadual, cfopSaidaBrindeBonificacaoDoacaoEstadual, cfopSaidaBrindeBonificacaoDoacaoEstadualComSt, cfopSaidaNaoContribuinteInterestadual, cfopBonificacaoExportacao, baseCalculoSt, ipiSaida, cfopSaidaInterestadual, cfopSaidaBrindeBonificacaoDoacaoInterestadual, cfopSaidaNaoContribuinteInterestadualOutro, cfopBonificacaoExportacaoOutro]);

    // --- 9. Listas e Filtros Memoizados ---
    const mpRawMaterials = useMemo(() => {
        if (!Array.isArray(allRawMaterials)) return [];
        return [...allRawMaterials].sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    }, [allRawMaterials]);

    const sortedQualityCriteria = useMemo(() => {
        if (!Array.isArray(allQualityCriteria)) return [];
        return [...allQualityCriteria].sort((a, b) => (a.analysis || '').localeCompare(b.analysis || ''));
    }, [allQualityCriteria]);

    const rawMaterialOptions = useMemo(() => {
        if (!mpRawMaterials) return [];

        const embalagemTypeId = allProductTypes.find(t => t.name?.toLowerCase() === 'embalagem')?.id;
        const embalagemCategoryId = allCategories.find(c => c.name?.toLowerCase() === 'embalagem')?.id;
        const serigrafiaTypeId = allProductTypes.find(t => t.name?.toLowerCase() === 'serigrafia')?.id;
        const serigrafiaCategoryId = allCategories.find(c => c.name?.toLowerCase() === 'serigrafia')?.id;
        const semiAcabadoCategoryId = allCategories.find(c => c.name?.toUpperCase() === 'SEMI ACABADO')?.id;

        const filteredMPs = mpRawMaterials.filter(mp => {
            const isMPCode = mp.code && typeof mp.code === 'string' && mp.code.toUpperCase().startsWith('MP');
            const isEmbalagem = (embalagemTypeId && mp.productTypeIds?.includes(embalagemTypeId)) ||
                (embalagemCategoryId && mp.categoryIds?.includes(embalagemCategoryId));
            const isSerigrafia = (serigrafiaTypeId && mp.productTypeIds?.includes(serigrafiaTypeId)) ||
                (serigrafiaCategoryId && mp.categoryIds?.includes(serigrafiaCategoryId));

            if (!isMPCode && !isEmbalagem && !isSerigrafia) return false;

            const matchesType = !filterProductTypeId || (mp.productTypeIds || []).includes(filterProductTypeId);
            const matchesCategory = !filterCategoryId || (mp.categoryIds || []).includes(filterCategoryId);

            return matchesType && matchesCategory;
        });

        const semiAcabados = (products || []).filter(p => {
            const isSemi = semiAcabadoCategoryId && p.categoryIds?.includes(semiAcabadoCategoryId);
            if (!isSemi) return false;
            const matchesType = !filterProductTypeId || (p.productTypeIds || []).includes(filterProductTypeId);
            const matchesCategory = !filterCategoryId || (p.categoryIds || []).includes(filterCategoryId);
            return matchesType && matchesCategory;
        });

        const combinedOptions = [
            ...filteredMPs.map(mp => {
                const commercialName = mp.commercialName || '';
                const inciName = mp.inciName || '';
                let label = `[MP] ${mp.code} - `;
                if (commercialName) {
                    label += commercialName;
                    if (inciName && inciName !== commercialName) {
                        label += ` (${inciName})`;
                    }
                } else if (inciName) {
                    label += inciName;
                } else {
                    label += 'N/A';
                }
                return {
                    value: mp.id,
                    label: label,
                    source: 'raw-materials',
                    searchData: { commercialName, inciName, code: mp.code }
                };
            }),
            ...semiAcabados.map(p => ({
                value: p.id,
                label: `[SA] ${p.code} - ${p.commercialName}`,
                source: 'products',
                searchData: { commercialName: p.commercialName, code: p.code }
            }))
        ];

        return combinedOptions.sort((a, b) => a.label.localeCompare(b.label));
    }, [mpRawMaterials, products, filterProductTypeId, filterCategoryId, allProductTypes, allCategories]);

    // --- Funcao para validar codigo antes de salvar ---
    const validateCodeBeforeSave = useCallback(() => {
        if (!code) {
            setFormStatus({ message: t('pages.products.form.codeRequired'), type: 'error' });
            return false;
        }
        if (!sigla) {
            setFormStatus({ message: t('pages.products.form.acronymRequired'), type: 'error' });
            return false;
        }
        if (!code.startsWith(sigla.toUpperCase())) {
            setFormStatus({ message: t('pages.products.form.codeMustStartWithAcronym', { acronym: sigla.toUpperCase() }), type: 'error' });
            return false;
        }
        const numberPart = code.slice(sigla.length);
        if (!numberPart || !/^\d+$/.test(numberPart)) {
            setFormStatus({ message: t('pages.products.form.codeNumbersOnly'), type: 'error' });
            return false;
        }
        if (!isCodeUnique(code, editingItem?.id)) {
            setFormStatus({ message: t('pages.products.form.codeAlreadyExists', { code }), type: 'error' });
            return false;
        }
        return true;
    }, [code, sigla, isCodeUnique, editingItem, t]);

    // --- Funcoes de Manipulacao de Tabelas ---
    const handleAddRawMaterial = (selectedOption) => {
        if (!selectedOption) return;

        const itemId = selectedOption.value;
        const isSemiAcabado = selectedOption.source === 'products';

        let itemToAdd = isSemiAcabado
            ? products.find(p => p.id === itemId)
            : allRawMaterials.find(mp => mp.id === itemId);

        if (itemToAdd) {
            let valorCusto = 0;

            if (isSemiAcabado) {
                valorCusto = (itemToAdd.totalFormulationCost || 0);
            } else {
                if (itemToAdd.custoCompra !== undefined && itemToAdd.custoCompra !== null) {
                    valorCusto = parseFloat(itemToAdd.custoCompra) || 0;
                }
                if (valorCusto === 0 && itemToAdd.suppliers && itemToAdd.suppliers[0]) {
                    valorCusto = parseFloat(itemToAdd.suppliers[0].valor) || 0;
                }
            }

            const displayName = itemToAdd.commercialName || itemToAdd.inciName || 'N/A';

            const newComponent = {
                rowId: crypto.randomUUID(),
                id: itemToAdd.id,
                order: rawMaterialsList.length + 1,
                fase: '',
                inciName: displayName,
                code: itemToAdd.code,
                valor: valorCusto,
                quantity: '',
                isSemiAcabado: isSemiAcabado,
                productTypeIds: itemToAdd.productTypeIds || [],
                categoryIds: itemToAdd.categoryIds || [],
            };
            setRawMaterialsList(prev => [...prev, newComponent]);
        }
    };

    const handleRemoveRawMaterial = (rowIdToRemove) => {
        const updatedList = rawMaterialsList
            .filter(mp => mp.rowId !== rowIdToRemove)
            .map((mp, index) => ({ ...mp, order: index + 1 }));
        setRawMaterialsList(updatedList);
    };

    const handleUpdateRawMaterialField = (rowId, field, value) => {
        setRawMaterialsList(prev => prev.map(mp =>
            mp.rowId === rowId ? { ...mp, [field]: value } : mp
        ));
    };

    const handleMoveRawMaterial = (index, direction) => {
        const list = [...rawMaterialsList];
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) return;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [list[index], list[newIndex]] = [list[newIndex], list[index]];
        list[index].order = index + 1;
        list[newIndex].order = newIndex + 1;
        setRawMaterialsList(list);
    };

    const handleAddCriteria = (e) => {
        const criteriaId = e.target.value;
        if (!criteriaId || qualityCriteria.find(c => c.id === criteriaId)) return;
        const criteriaToAdd = allQualityCriteria.find(c => c.id === criteriaId);
        if (criteriaToAdd) {
            const newCriteria = {
                id: criteriaToAdd.id,
                analysis: criteriaToAdd.analysis,
                method: criteriaToAdd.method,
                specification: criteriaToAdd.specification || '',
                control: ANALISIS_CONTROL_OPTIONS[0].value,
            };
            setQualityCriteria(prev => [...prev, newCriteria]);
            e.target.value = '';
        }
    };

    const handleRemoveCriteria = (idToRemove) => {
        const updatedCriteria = qualityCriteria.filter(c => c.id !== idToRemove);
        setQualityCriteria(updatedCriteria);
    };

    const handleUpdateCriteriaField = (id, field, value) => {
        setQualityCriteria(prev => prev.map(c =>
            c.id === id ? { ...c, [field]: value } : c
        ));
    };

    const handleMoveCriteria = (index, direction) => {
        const list = [...qualityCriteria];
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) return;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [list[index], list[newIndex]] = [list[newIndex], list[index]];
        setQualityCriteria(list);
    };

    // --- Handlers for Modo de Preparo ---
    const handleAddPreparationStep = () => {
        const trimmedStep = currentStep.trim();
        if (!trimmedStep) return;

        const newStep = {
            id: crypto.randomUUID(),
            order: preparationSteps.length + 1,
            description: trimmedStep
        };

        setPreparationSteps(prev => [...prev, newStep]);
        setCurrentStep('');
        setSelectedSetup('');
    };

    const handleUpdatePreparationStep = (id, value) => {
        setPreparationSteps(prev => prev.map(step =>
            step.id === id ? { ...step, description: value } : step
        ));
        setSelectedSetup('');
    };

    const handleRemovePreparationStep = (idToRemove) => {
        const updatedSteps = preparationSteps
            .filter(step => step.id !== idToRemove)
            .map((step, index) => ({ ...step, order: index + 1 }));
        setPreparationSteps(updatedSteps);
        setSelectedSetup('');
    };

    const handleMovePreparationStep = (index, direction) => {
        const list = [...preparationSteps];
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) return;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [list[index], list[newIndex]] = [list[newIndex], list[index]];
        const updatedList = list.map((step, i) => ({ ...step, order: i + 1 }));
        setPreparationSteps(updatedList);
        setSelectedSetup('');
    };

    const handleStepKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddPreparationStep();
        }
    };

    // --- Handler para carregar Setup de Preparo ---
    const handleLoadSetup = (setupId) => {
        if (!setupId) {
            setSelectedSetup('');
            return;
        }

        const setup = preparationSetups.find(s => s.id === setupId);
        if (setup && setup.preparationSteps) {
            const stepsWithIds = setup.preparationSteps.map(step => ({
                id: crypto.randomUUID(),
                order: step.order,
                description: step.description
            }));
            setPreparationSteps(stepsWithIds);
            setSelectedSetup(setupId);
        }
    };

    // --- Handler para importar MPs de outro produto ---
    const handleImportMPs = (sourceProduct) => {
        if (!sourceProduct.rawMaterials || sourceProduct.rawMaterials.length === 0) {
            addToast(t('pages.products.importModal.noMaterials'), 'error');
            return;
        }

        const updatedMaterials = sourceProduct.rawMaterials.map((mp, index) => {
            const currentPrice = getLatestPrice(mp.id, mp.valor);
            return {
                ...mp,
                rowId: crypto.randomUUID(),
                valor: currentPrice,
                order: rawMaterialsList.length + index + 1
            };
        });

        setRawMaterialsList(prev => [...prev, ...updatedMaterials]);
        setShowImportMPModal(false);
        setImportSearchTerm('');
        addToast(t('pages.products.importModal.importSuccess', { count: updatedMaterials.length }), 'success');
    };

    const handleExportRawMaterials = () => {
        if (rawMaterialsList.length === 0) return;

        const commercialNames = rawMaterialsList
            .map((mp) => {
                let mpData;
                if (mp.isSemiAcabado) {
                    mpData = products.find(p => p.id === mp.id);
                } else {
                    mpData = allRawMaterials.find(m => m.id === mp.id);
                }
                if (!mpData) return null;
                return mpData.inciName || '';
            })
            .filter(name => name && name.trim() !== '');

        if (commercialNames.length === 0) {
            addToast(t('pages.products.exportNoNamesFound'), 'warning');
            return;
        }

        const textContent = commercialNames.join(';');
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const fileName = `commercial-names-${code || 'produto'}-${new Date().toISOString().split('T')[0]}.txt`;

        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        addToast(t('pages.products.exportSuccess'), 'success');
    };

    // --- Funcoes de Formulario (Reset e Salvar) ---
    const resetForm = () => {
        setEditingItem(null);
        setIsModalOpen(false);
        setSigla('');
        setCode('');
        setRecommendedCode('');
        setCommercialName('');
        setSituation(DEFAULT_SITUATION);
        setBarcode('');
        setNetWeight('');
        setGrossWeight('');
        setDensity('');
        setLossIndex('');
        setSelectedProductTypeIds([]);
        setSelectedCategoryIds([]);
        setRawMaterialsList([]);
        setQualityCriteria([]);
        setPreparationSteps([]);
        setCurrentStep('');
        setSelectedSetup('');
        setMarkup('1.00');
        setFormStatus({ message: '', type: '' });
        setFilterProductTypeId('');
        setFilterCategoryId('');
        setShowImportMPModal(false);
        setImportSearchTerm('');
        setGramatura('');
        setGramaturaUnit('g');
        setShelfLifeMonths('');
        setSelectedProductionPhases([]);
        // Reset box type configs
        setBoxTypeConfigs([]);
        setOrigemOperacaoComercial('Fabricação Própria');
        setOrigemMercadoriaIcms('0');
        setSelectedNcmId('');
        setExcecaoFiscalIpi(false);
        setExcecaoFiscalIva(false);
        setExcecaoFiscalPisCofins(false);
        setValorPautaRs('');
        setClassificacaoTributariaRt('');
        setCfopSaidaEstadual('');
        setCfopSaidaEstadualComSt('');
        setCfopIndustrializacaoEstadual('');
        setCfopSaidaBrindeBonificacaoDoacaoEstadual('');
        setCfopSaidaBrindeBonificacaoDoacaoEstadualComSt('');
        setCfopSaidaNaoContribuinteInterestadual('');
        setCfopBonificacaoExportacao('');
        setBaseCalculoSt('');
        setIpiSaida('');
        setCfopSaidaInterestadual('');
        setCfopSaidaBrindeBonificacaoDoacaoInterestadual('');
        setCfopSaidaNaoContribuinteInterestadualOutro('');
        setCfopBonificacaoExportacaoOutro('');
        setCfopExportacao('');

        originalItemDataRef.current = null;
    };

    const handleDeleteClick = async (itemId) => {
        if (!can.delete) {
            addToast(t('pages.products.noPermissionDelete'), 'error');
            return;
        }

        if (window.confirm(t('pages.products.deleteConfirm'))) {
            try {
                const result = await deleteDocument(itemId);
                if (!result.success) {
                    addToast(`${t('pages.products.deleteError')}: ${result.error || t('common.unknownError')}`, 'error');
                } else {
                    addToast(t('pages.products.deleteSuccess'), 'success');
                }
            } catch (err) {
                addToast(`${t('pages.products.deleteError')}: ${err.message}`, 'error');
            }
        }
    };

    // --- Function to calculate box weights for a specific box type ---
    const calculateBoxWeightsForType = useCallback((boxTypeId, unitsPerBoxValue) => {
        if (!boxTypeId || !unitsPerBoxValue || parseInt(unitsPerBoxValue) <= 0) {
            return { boxNetWeight: 0, boxGrossWeight: 0, boxVolume: 0 };
        }

        const unitsPerBoxNum = parseInt(unitsPerBoxValue, 10);
        const unitNetWeight = parseFloat(netWeight) || 0;
        const unitGrossWeight = parseFloat(grossWeight) || 0;

        // Get box dimensions to calculate volume if needed
        const selectedBox = boxTypes.find(b => b.id === boxTypeId);
        let boxVolume = 0;
        if (selectedBox) {
            const height = selectedBox.height || selectedBox.boxHeight || 0;
            const width = selectedBox.width || selectedBox.boxWidth || 0;
            const length = selectedBox.length || selectedBox.boxLength || 0;
            boxVolume = (height * width * length) / 1000000; // Convert to m³
        }

        return {
            boxNetWeight: unitNetWeight * unitsPerBoxNum,
            boxGrossWeight: unitGrossWeight * unitsPerBoxNum,
            boxVolume: boxVolume
        };
    }, [netWeight, grossWeight, boxTypes]);

    const handleSave = async (e) => {
        e.preventDefault();

        if (!editingItem && !can.create) {
            setFormStatus({ message: t('pages.products.noPermissionCreate'), type: 'error' });
            addToast(t('pages.products.noPermissionCreate'), 'error');
            return;
        }
        if (editingItem && !can.edit) {
            setFormStatus({ message: t('pages.products.noPermissionEdit'), type: 'error' });
            addToast(t('pages.products.noPermissionEdit'), 'error');
            return;
        }

        setFormStatus({ message: '', type: '' });

        if (editingItem && !isFormDirty()) {
            addToast(t('pages.products.noChangesDetected'), 'info');
            setTimeout(() => resetForm(), 1000);
            return;
        }

        if (!sigla || !code || !commercialName) {
            setFormStatus({ message: t('pages.products.form.requiredFields'), type: 'error' });
            return;
        }

        if (!validateCodeBeforeSave()) {
            return;
        }

        if (barcode && !validateBarcode(barcode)) {
            setFormStatus({
                message: t('pages.products.form.barcodeNumericOnly'),
                type: 'error'
            });
            return;
        }

        if (shelfLifeMonths && (parseFloat(shelfLifeMonths) <= 0 || isNaN(parseFloat(shelfLifeMonths)))) {
            setFormStatus({ message: t('pages.products.form.shelfLifePositive'), type: 'error' });
            return;
        }

        // Validate that all box type configs have unitsPerBox > 0
        for (const config of boxTypeConfigs) {
            if (!config.unitsPerBox || parseInt(config.unitsPerBox) <= 0) {
                setFormStatus({
                    message: t('pages.products.form.unitsPerBoxRequiredForType', { boxName: config.boxName || config.boxTypeId }),
                    type: 'error'
                });
                return;
            }
        }

        const unitCategoryIds = allCategories
            .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
            .map(c => c.id);

        const isUnitCalculation = (mpId) => {
            const mpData = allRawMaterials.find(item => item.id === mpId);
            return mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
        };

        const totalQuantity = rawMaterialsList.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                return acc + (parseFloat(mp.quantity) || 0);
            }
            return acc;
        }, 0);

        const hasPercentageMaterials = rawMaterialsList.some(mp => !isUnitCalculation(mp.id));

        if (hasPercentageMaterials && Math.abs(totalQuantity - 100) > 0.01) {
            setFormStatus({
                message: t('pages.products.form.percentageSumError', { total: totalQuantity.toFixed(2) }),
                type: 'error'
            });
            return;
        }

        setFormLoading(true);
        try {
            const rawMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
                if (!isUnitCalculation(mp.id)) {
                    const price = getLatestPrice(mp.id, mp.valor);
                    const quantity = parseFloat(mp.quantity) || 0;
                    return acc + (price * quantity) / 100;
                }
                return acc;
            }, 0);

            const rawMaterialsTotalCost = rawMaterialsCost * (parseFloat(netWeight) || 0);
            const unitMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
                if (isUnitCalculation(mp.id)) {
                    const price = getLatestPrice(mp.id, mp.valor);
                    const quantity = parseFloat(mp.quantity) || 0;
                    return acc + (price * quantity);
                }
                return acc;
            }, 0);

            const currentTotalCost = rawMaterialsTotalCost + unitMaterialsCost;

            // Calculate box weights for each box type config
            const boxWeightsMap = {};
            boxTypeConfigs.forEach(config => {
                const weights = calculateBoxWeightsForType(config.boxTypeId, config.unitsPerBox);
                boxWeightsMap[config.boxTypeId] = weights;
            });

            // Prepare box type configs for saving
            const boxConfigsToSave = boxTypeConfigs.map(config => ({
                boxTypeId: config.boxTypeId,
                unitsPerBox: parseInt(config.unitsPerBox) || 0
            }));

            const itemToSave = {
                sigla: sigla.toUpperCase().trim(),
                code: code.toUpperCase().trim(),
                commercialName: commercialName.trim(),
                situation: situation,
                barcode: barcode.trim(),
                netWeight: parseFloat(netWeight) || 0,
                grossWeight: parseFloat(grossWeight) || 0,
                density: parseFloat(density) || 0,
                lossIndex: parseFloat(lossIndex) || 0,
                productTypeIds: selectedProductTypeIds,
                categoryIds: selectedCategoryIds,
                rawMaterials: rawMaterialsList,
                qualityCriteria: qualityCriteria,
                preparationSteps: preparationSteps,
                markup: parseFloat(markup) || 1,
                totalFormulationCost: currentTotalCost,
                commercialData: {},
                technicalSpec: {},
                technical: {
                    gramatura: parseFloat(gramatura) || 0,
                    gramaturaUnit: gramaturaUnit || 'g',
                    shelfLifeMonths: shelfLifeMonths ? parseFloat(shelfLifeMonths) : null,
                    productionPhases: selectedProductionPhases,
                    // Save multiple box types with their units
                    boxTypeConfigs: boxConfigsToSave,
                    // For backward compatibility, keep single boxTypeId and unitsPerBox from first config
                    boxTypeId: boxConfigsToSave.length > 0 ? boxConfigsToSave[0].boxTypeId : null,
                    unitsPerBox: boxConfigsToSave.length > 0 ? boxConfigsToSave[0].unitsPerBox : null,
                    boxWeights: boxWeightsMap,
                },
                fiscal: {
                    origemOperacaoComercial,
                    origemMercadoriaIcms,
                    ncmId: selectedNcmId,
                    excecaoFiscalIpi,
                    excecaoFiscalIva,
                    excecaoFiscalPisCofins,
                    valorPautaRs: parseFloat(valorPautaRs) || 0,
                    classificacaoTributariaRt,
                    cfopSaidaEstadual,
                    cfopSaidaEstadualComSt,
                    cfopIndustrializacaoEstadual,
                    cfopSaidaBrindeBonificacaoDoacaoEstadual,
                    cfopSaidaBrindeBonificacaoDoacaoEstadualComSt,
                    cfopSaidaNaoContribuinteInterestadual,
                    cfopBonificacaoExportacao,
                    baseCalculoSt: parseFloat(baseCalculoSt) || 0,
                    ipiSaida: parseFloat(ipiSaida) || 0,
                    cfopSaidaInterestadual,
                    cfopSaidaBrindeBonificacaoDoacaoInterestadual,
                    cfopSaidaNaoContribuinteInterestadualOutro,
                    cfopBonificacaoExportacaoOutro,
                    cfopExportacao,
                },
            };

            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, itemToSave);
                if (result.success) {
                    setProductsVersion(prev => prev + 1);
                    if (refreshProducts) {
                        await refreshProducts();
                    }
                    addToast(t('pages.products.updateSuccess'), 'success');
                    setTimeout(() => resetForm(), 1000);
                } else {
                    addToast(`${t('pages.products.updateError')}: ${result.error}`, 'error');
                }
            } else {
                result = await createDocument(itemToSave);
                if (result.success) {
                    setProductsVersion(prev => prev + 1);
                    if (refreshProducts) {
                        await refreshProducts();
                    }
                    addToast(t('pages.products.createSuccess'), 'success');
                    setTimeout(() => resetForm(), 1000);
                } else {
                    addToast(`${t('pages.products.createError')}: ${result.error}`, 'error');
                }
            }
        } catch (error) {
            addToast(`${t('pages.products.saveError')}: ${error.message}`, 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleEditClick = (item) => {
        if (!can.edit) {
            addToast(t('pages.products.noPermissionEdit'), 'error');
            return;
        }

        const technical = item.technical || {};

        // Load box type configs from saved data
        let loadedBoxConfigs = [];
        if (technical.boxTypeConfigs && Array.isArray(technical.boxTypeConfigs)) {
            loadedBoxConfigs = technical.boxTypeConfigs.map(config => {
                const box = boxTypes.find(b => b.id === config.boxTypeId);
                return {
                    boxTypeId: config.boxTypeId,
                    unitsPerBox: config.unitsPerBox || 1,
                    boxName: box ? box.name : config.boxTypeId,
                    boxDetails: box || null
                };
            });
        } else if (technical.boxTypeId) {
            // Backward compatibility: single box type
            const box = boxTypes.find(b => b.id === technical.boxTypeId);
            loadedBoxConfigs = [{
                boxTypeId: technical.boxTypeId,
                unitsPerBox: technical.unitsPerBox || 1,
                boxName: box ? box.name : technical.boxTypeId,
                boxDetails: box || null
            }];
        }

        originalItemDataRef.current = {
            sigla: item.sigla || '',
            code: item.code || '',
            commercialName: item.commercialName || '',
            situation: item.situation || DEFAULT_SITUATION,
            barcode: item.barcode || '',
            netWeight: item.netWeight || '',
            grossWeight: item.grossWeight || '',
            density: item.density || '',
            lossIndex: item.lossIndex || '',
            productTypeIds: item.productTypeIds || [],
            categoryIds: item.categoryIds || [],
            rawMaterials: item.rawMaterials || [],
            qualityCriteria: item.qualityCriteria || [],
            preparationSteps: item.preparationSteps || [],
            markup: item.markup?.toString() || '1.00',
            gramatura: technical.gramatura || '',
            gramaturaUnit: technical.gramaturaUnit || 'g',
            shelfLifeMonths: technical.shelfLifeMonths || '',
            productionPhases: technical.productionPhases || [],
            boxTypeConfigs: loadedBoxConfigs,
            fiscal: item.fiscal || {},
        };

        setEditingItem(item);
        setSigla(item.sigla || '');
        setCode(item.code || '');
        setRecommendedCode('');
        setCommercialName(item.commercialName || '');
        setSituation(item.situation || DEFAULT_SITUATION);
        setBarcode(item.barcode || '');
        setNetWeight(item.netWeight || '');
        setGrossWeight(item.grossWeight || '');
        setDensity(item.density || '');
        setLossIndex(item.lossIndex || '');
        setSelectedProductTypeIds(item.productTypeIds || []);
        setSelectedCategoryIds(item.categoryIds || []);
        setRawMaterialsList(item.rawMaterials || []);
        setQualityCriteria(item.qualityCriteria || []);
        setPreparationSteps(item.preparationSteps || []);
        setMarkup(item.markup?.toString() || '1.00');
        setSelectedSetup('');

        setGramatura(technical.gramatura?.toString() || '');
        setGramaturaUnit(technical.gramaturaUnit || 'g');
        setShelfLifeMonths(technical.shelfLifeMonths?.toString() || '');
        setSelectedProductionPhases(technical.productionPhases || []);
        // Set box type configs
        setBoxTypeConfigs(loadedBoxConfigs);

        const fiscal = item.fiscal || {};
        setOrigemOperacaoComercial(fiscal.origemOperacaoComercial || 'Fabricação Própria');
        setOrigemMercadoriaIcms(fiscal.origemMercadoriaIcms || '0');
        setSelectedNcmId(fiscal.ncmId || '');
        setExcecaoFiscalIpi(fiscal.excecaoFiscalIpi || false);
        setExcecaoFiscalIva(fiscal.excecaoFiscalIva || false);
        setExcecaoFiscalPisCofins(fiscal.excecaoFiscalPisCofins || false);
        setValorPautaRs(fiscal.valorPautaRs?.toString() || '');
        setClassificacaoTributariaRt(fiscal.classificacaoTributariaRt || '');
        setCfopSaidaEstadual(fiscal.cfopSaidaEstadual || '');
        setCfopSaidaEstadualComSt(fiscal.cfopSaidaEstadualComSt || '');
        setCfopIndustrializacaoEstadual(fiscal.cfopIndustrializacaoEstadual || '');
        setCfopSaidaBrindeBonificacaoDoacaoEstadual(fiscal.cfopSaidaBrindeBonificacaoDoacaoEstadual || '');
        setCfopSaidaBrindeBonificacaoDoacaoEstadualComSt(fiscal.cfopSaidaBrindeBonificacaoDoacaoEstadualComSt || '');
        setCfopSaidaNaoContribuinteInterestadual(fiscal.cfopSaidaNaoContribuinteInterestadual || '');
        setCfopBonificacaoExportacao(fiscal.cfopBonificacaoExportacao || '');
        setBaseCalculoSt(fiscal.baseCalculoSt?.toString() || '');
        setIpiSaida(fiscal.ipiSaida?.toString() || '');
        setCfopSaidaInterestadual(fiscal.cfopSaidaInterestadual || '');
        setCfopSaidaBrindeBonificacaoDoacaoInterestadual(fiscal.cfopSaidaBrindeBonificacaoDoacaoInterestadual || '');
        setCfopSaidaNaoContribuinteInterestadualOutro(fiscal.cfopSaidaNaoContribuinteInterestadualOutro || '');
        setCfopBonificacaoExportacaoOutro(fiscal.cfopBonificacaoExportacaoOutro || '');
        setCfopExportacao(fiscal.cfopExportacao || '');

        setFormStatus({ message: '', type: '' });
        setIsModalOpen(true);
        setActiveTab(TABS.TECNICO);
    };

    const qualityCriteriaOptions = useMemo(() => {
        if (!sortedQualityCriteria || sortedQualityCriteria.length === 0) return [];
        return sortedQualityCriteria.map(c => ({
            value: c.id,
            label: `${c.analysis} - ${c.method}`,
        }));
    }, [sortedQualityCriteria]);

    // --- FUNCAO PARA CALCULAR O PRECO SUGERIDO ---
    const calculateSuggestedPrice = useCallback((item) => {
        if (!item || !item.rawMaterials || item.rawMaterials.length === 0) {
            const totalCost = parseFloat(item?.totalFormulationCost) || 0;
            const markup = parseFloat(item?.markup) || 1;
            return totalCost * markup;
        }

        const unitCategoryIds = allCategories
            .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
            .map(c => c.id);

        const isUnitCalculation = (mpId) => {
            const mpData = allRawMaterials.find(mp => mp.id === mpId);
            return mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
        };

        const rawMaterialsCost = item.rawMaterials.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                const price = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (price * quantity) / 100;
            }
            return acc;
        }, 0);

        const netWeightVal = parseFloat(item.netWeight) || 0;
        const rawMaterialsTotalCost = rawMaterialsCost * netWeightVal;

        const unitMaterialsCost = item.rawMaterials.reduce((acc, mp) => {
            if (isUnitCalculation(mp.id)) {
                const price = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (price * quantity);
            }
            return acc;
        }, 0);

        const totalCost = rawMaterialsTotalCost + unitMaterialsCost;
        const markup = parseFloat(item.markup) || 1;
        return totalCost * markup;
    }, [allCategories, allRawMaterials, getLatestPrice]);

    // --- LOGICA DE FILTRAGEM E PAGINACAO ---
    const filteredProducts = useMemo(() => {
        if (!products) return [];
        const lowerSearch = mainSearchTerm.toLowerCase();

        return products.filter(item => {
            const matchesSearchTerm = (
                (item.commercialName && item.commercialName.toLowerCase().includes(lowerSearch)) ||
                (item.code && item.code.toLowerCase().includes(lowerSearch)) ||
                (item.sigla && item.sigla.toLowerCase().includes(lowerSearch)) ||
                (item.barcode && item.barcode.toLowerCase().includes(lowerSearch))
            );

            const matchesProductType = !mainFilterProductTypeId || (item.productTypeIds && item.productTypeIds.includes(mainFilterProductTypeId));
            const matchesCategory = !mainFilterCategoryId || (item.categoryIds && item.categoryIds.includes(mainFilterCategoryId));

            return matchesSearchTerm && matchesProductType && matchesCategory;
        }).sort((a, b) => {
            const codeA = a.code || '';
            const codeB = b.code || '';
            return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
        });
    }, [products, mainSearchTerm, mainFilterProductTypeId, mainFilterCategoryId, productsVersion]);

    const {
        currentPage,
        totalPages,
        currentItems: currentProducts,
        goToPage,
        resetPage,
    } = usePagination(filteredProducts, ITEMS_PER_PAGE);

    const handleMainFilterChange = (setter, value) => {
        setter(value);
        resetPage();
    };

    useEffect(() => {
        resetPage();
    }, [mainSearchTerm, mainFilterProductTypeId, mainFilterCategoryId, resetPage]);

    // --- Renderizacao dos Componentes Internos ---

    // Renderizacao da Aba Tecnico
    const renderTechnicianTab = () => {
        // Prepare box type options for TagInput
        const boxTypeOptions = boxTypes
            .filter(box => box.situation !== 'Inativo')
            .map(box => ({
                id: box.id,
                name: box.name,
                productTypeName: getBoxProductTypeName(box.productTypeId)
            }));

        // Get currently selected box type IDs for the TagInput
        const selectedBoxTypeIds = boxTypeConfigs.map(config => config.boxTypeId);

        return (
            <div className={styles.tabSection}>
                <div className={styles.subSection}>
                    <div className={styles.subSectionHeader}>
                        <div className={styles.subSectionIcon}><FaBox size={18} /></div>
                        <h4 className={styles.subSectionTitle}>{t('pages.products.technical.boxTypes')}</h4>
                    </div>

                    <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                        <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                            <TagInput
                                label={t('pages.products.technical.boxTypesLabel')}
                                options={boxTypeOptions.map(opt => ({ id: opt.id, name: `${opt.name} (${opt.productTypeName})` }))}
                                selectedIds={selectedBoxTypeIds}
                                onAdd={(option) => {
                                    if (option && !selectedBoxTypeIds.includes(option.id)) {
                                        const box = boxTypes.find(b => b.id === option.id);
                                        if (box) {
                                            setBoxTypeConfigs(prev => [...prev, {
                                                boxTypeId: option.id,
                                                unitsPerBox: 1,
                                                boxName: box.name,
                                                boxDetails: box
                                            }]);
                                        }
                                    }
                                }}
                                onRemove={(option) => {
                                    setBoxTypeConfigs(prev => prev.filter(config => config.boxTypeId !== option.id));
                                }}
                                placeholder={t('pages.products.technical.boxTypesPlaceholder')}
                                disabled={formLoading || auxiliaryDataLoading || !can.edit}
                                getOptionLabel={(item) => item.name}
                                getOptionId={(item) => item.id}
                            />
                            <small className={styles.fieldHint}>{t('pages.products.technical.boxTypesHint')}</small>
                        </div>
                    </div>

                    {boxTypeConfigs.length > 0 && (
                        <div className={styles.boxWeightInfo}>
                            <div className={styles.subSection} style={{ marginTop: '15px' }}>
                                <h5 style={{ marginBottom: '12px', color: 'var(--text-secondary)' }}>
                                    {t('pages.products.technical.boxConfigsTitle')}
                                </h5>
                                <div className={styles.formGrid}>
                                    {boxTypeConfigs.map((config) => {
                                        const box = boxTypes.find(b => b.id === config.boxTypeId);
                                        const productTypeName = box ? getBoxProductTypeName(box.productTypeId) : '';
                                        const weights = calculateBoxWeightsForType(config.boxTypeId, config.unitsPerBox);

                                        return (
                                            <div key={config.boxTypeId} className={styles.inputGroup} style={{
                                                backgroundColor: '#f8f9fa',
                                                padding: '12px',
                                                borderRadius: '6px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                    <label style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                                        {config.boxName || box?.name || config.boxTypeId}
                                                        {productTypeName && (
                                                            <span style={{ fontWeight: 'normal', fontSize: '0.85rem', color: '#666', marginLeft: '6px' }}>
                                                                ({productTypeName})
                                                            </span>
                                                        )}
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveBoxType(config.boxTypeId)}
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        disabled={formLoading || !can.edit}
                                                        style={{ padding: '2px 8px' }}
                                                    >
                                                        <FiX size={14} />
                                                    </button>
                                                </div>

                                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                                    <div style={{ flex: '1', minWidth: '150px' }}>
                                                        <label style={{ fontSize: '0.8rem', color: '#666' }}>
                                                            {t('pages.products.technical.unitsPerBox')} *
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            value={config.unitsPerBox}
                                                            onChange={(e) => handleUpdateUnitsPerBox(config.boxTypeId, e.target.value)}
                                                            disabled={formLoading || !can.edit}
                                                            className={styles.input}
                                                            style={{ width: '80px', display: 'inline-block', marginLeft: '8px' }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <span style={{ fontSize: '0.8rem', color: '#666' }}>{t('pages.products.technical.boxNetWeight')}:</span>
                                                        <span style={{ fontWeight: 'bold', marginLeft: '6px' }}>
                                                            {weights.boxNetWeight.toFixed(3)} Kg
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span style={{ fontSize: '0.8rem', color: '#666' }}>{t('pages.products.technical.boxGrossWeight')}:</span>
                                                        <span style={{ fontWeight: 'bold', marginLeft: '6px' }}>
                                                            {weights.boxGrossWeight.toFixed(3)} Kg
                                                        </span>
                                                    </div>
                                                    {weights.boxVolume > 0 && (
                                                        <div>
                                                            <span style={{ fontSize: '0.8rem', color: '#666' }}>Volume:</span>
                                                            <span style={{ fontWeight: 'bold', marginLeft: '6px' }}>
                                                                {weights.boxVolume.toFixed(6)} m³
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <small className={styles.fieldHint} style={{ marginTop: '4px', display: 'block' }}>
                                                    {t('pages.products.technical.boxWeightsHint', {
                                                        unitWeight: parseFloat(netWeight) || 0,
                                                        units: config.unitsPerBox
                                                    })}
                                                </small>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="gramatura">{t('pages.products.technical.gramatura')}</label>
                        <div className={styles.inputGroupInline}>
                            <input
                                id="gramatura"
                                type="number"
                                step="0.001"
                                min="0"
                                value={gramatura}
                                onChange={(e) => setGramatura(e.target.value)}
                                disabled={formLoading || !can.edit}
                                className={styles.input}
                                placeholder={t('pages.products.technical.gramaturaPlaceholder')}
                            />
                            <select
                                value={gramaturaUnit}
                                onChange={(e) => setGramaturaUnit(e.target.value)}
                                disabled={formLoading || !can.edit}
                                className={styles.unitSelect}
                            >
                                <option value="g">g</option>
                                <option value="Kg">Kg</option>
                                <option value="ml">ml</option>
                                <option value="L">L</option>
                            </select>
                        </div>
                        <small className={styles.fieldHint}>{t('pages.products.technical.gramaturaHint')}</small>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="shelfLife">{t('pages.products.technical.shelfLife')}</label>
                        <input
                            id="shelfLife"
                            type="number"
                            step="1"
                            min="0"
                            value={shelfLifeMonths}
                            onChange={(e) => setShelfLifeMonths(e.target.value)}
                            disabled={formLoading || !can.edit}
                            className={styles.input}
                            placeholder={t('pages.products.technical.shelfLifePlaceholder')}
                        />
                        <small className={styles.fieldHint}>{t('pages.products.technical.shelfLifeHint')}</small>
                    </div>
                </div>

                <div className={styles.subSection}>
                    <div className={styles.subSectionHeader}>
                        <div className={styles.subSectionIcon}><FaCogs size={18} /></div>
                        <h4 className={styles.subSectionTitle}>{t('pages.products.technical.productionPhases')}</h4>
                    </div>
                    <div className={styles.checkboxGrid}>
                        {PRODUCTION_PHASES_OPTIONS.map(phase => (
                            <label key={phase.value} className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={selectedProductionPhases.includes(phase.value)}
                                    onChange={(e) => handleProductionPhaseChange(phase.value, e.target.checked)}
                                    disabled={formLoading || !can.edit}
                                />
                                {phase.label}
                            </label>
                        ))}
                    </div>
                    {selectedProductionPhases.length === 0 && (
                        <small className={styles.fieldHint}>{t('pages.products.technical.noPhasesSelected')}</small>
                    )}
                </div>
            </div>
        );
    };

    // Renderizacao da Aba Fiscal
    const renderFiscalTab = () => {
        const selectedNcm = allNcms.find(n => n.id === selectedNcmId);

        return (
            <div className={styles.tabSection}>
                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="origemOperacaoComercial">
                            {t('pages.products.fiscal.commercialOrigin')}
                        </label>
                        <select
                            id="origemOperacaoComercial"
                            value={origemOperacaoComercial}
                            onChange={(e) => setOrigemOperacaoComercial(e.target.value)}
                            disabled={formLoading || !can.edit}
                            className={styles.select}
                        >
                            {ORIGEM_OPERACAO_COMERCIAL_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="origemMercadoriaIcms">
                            {t('pages.products.fiscal.icmsOrigin')}
                        </label>
                        <select
                            id="origemMercadoriaIcms"
                            value={origemMercadoriaIcms}
                            onChange={(e) => setOrigemMercadoriaIcms(e.target.value)}
                            disabled={formLoading || !can.edit}
                            className={styles.select}
                        >
                            {ORIGEM_MERCADORIA_ICMS_OPTIONS.map(opt => (
                                <option key={opt.code} value={opt.code}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="ncmSelect">
                            {t('pages.products.fiscal.ncm')}
                        </label>
                        <select
                            id="ncmSelect"
                            value={selectedNcmId}
                            onChange={(e) => setSelectedNcmId(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('pages.products.fiscal.ncmPlaceholder')}</option>
                            {allNcms.map(ncm => (
                                <option key={ncm.id} value={ncm.id}>
                                    {ncm.ncmCode}
                                </option>
                            ))}
                        </select>
                        {selectedNcm && (
                            <small className={styles.fieldHint}>
                                {t('pages.products.fiscal.ncmSelected')}: {selectedNcm.ncmCode}
                            </small>
                        )}
                    </div>

                    <div className={styles.inputGroup}>
                        <label style={{ display: 'block', marginBottom: '8px' }}>{t('pages.products.fiscal.taxExceptions')}</label>
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={excecaoFiscalIpi}
                                    onChange={(e) => setExcecaoFiscalIpi(e.target.checked)}
                                    disabled={formLoading || !can.edit}
                                />
                                {t('pages.products.fiscal.ipiException')}
                            </label>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={excecaoFiscalIva}
                                    onChange={(e) => setExcecaoFiscalIva(e.target.checked)}
                                    disabled={formLoading || !can.edit}
                                />
                                {t('pages.products.fiscal.ivaException')}
                            </label>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={excecaoFiscalPisCofins}
                                    onChange={(e) => setExcecaoFiscalPisCofins(e.target.checked)}
                                    disabled={formLoading || !can.edit}
                                />
                                {t('pages.products.fiscal.pisCofinsException')}
                            </label>
                        </div>
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="valorPautaRs">
                            {t('pages.products.fiscal.pautaValue')}
                        </label>
                        <input
                            id="valorPautaRs"
                            type="number"
                            step="0.0001"
                            value={valorPautaRs}
                            onChange={(e) => setValorPautaRs(e.target.value)}
                            disabled={formLoading || !can.edit}
                            className={styles.input}
                            placeholder={t('pages.products.fiscal.pautaValuePlaceholder')}
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="classificacaoTributariaRt">
                            {t('pages.products.fiscal.taxClassification')}
                        </label>
                        <input
                            id="classificacaoTributariaRt"
                            type="text"
                            value={classificacaoTributariaRt}
                            onChange={(e) => setClassificacaoTributariaRt(e.target.value)}
                            disabled={formLoading || !can.edit}
                            className={styles.input}
                            placeholder={t('pages.products.fiscal.taxClassificationPlaceholder')}
                        />
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaEstadual">
                            {t('pages.products.fiscal.cfopStateOutput')}
                        </label>
                        <select
                            id="cfopSaidaEstadual"
                            value={cfopSaidaEstadual}
                            onChange={(e) => setCfopSaidaEstadual(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaEstadualComSt">
                            {t('pages.products.fiscal.cfopStateOutputWithSt')}
                        </label>
                        <select
                            id="cfopSaidaEstadualComSt"
                            value={cfopSaidaEstadualComSt}
                            onChange={(e) => setCfopSaidaEstadualComSt(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopIndustrializacaoEstadual">
                            {t('pages.products.fiscal.cfopIndustrialization')}
                        </label>
                        <select
                            id="cfopIndustrializacaoEstadual"
                            value={cfopIndustrializacaoEstadual}
                            onChange={(e) => setCfopIndustrializacaoEstadual(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopExportacao">
                            {t('pages.products.fiscal.cfopExport')}
                        </label>
                        <select
                            id="cfopExportacao"
                            value={cfopExportacao}
                            onChange={(e) => setCfopExportacao(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaBrindeBonificacaoDoacaoEstadual">
                            {t('pages.products.fiscal.cfopGiftState')}
                        </label>
                        <select
                            id="cfopSaidaBrindeBonificacaoDoacaoEstadual"
                            value={cfopSaidaBrindeBonificacaoDoacaoEstadual}
                            onChange={(e) => setCfopSaidaBrindeBonificacaoDoacaoEstadual(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaBrindeBonificacaoDoacaoEstadualComSt">
                            {t('pages.products.fiscal.cfopGiftStateWithSt')}
                        </label>
                        <select
                            id="cfopSaidaBrindeBonificacaoDoacaoEstadualComSt"
                            value={cfopSaidaBrindeBonificacaoDoacaoEstadualComSt}
                            onChange={(e) => setCfopSaidaBrindeBonificacaoDoacaoEstadualComSt(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaNaoContribuinteInterestadual">
                            {t('pages.products.fiscal.cfopNonContributorInterstate')}
                        </label>
                        <select
                            id="cfopSaidaNaoContribuinteInterestadual"
                            value={cfopSaidaNaoContribuinteInterestadual}
                            onChange={(e) => setCfopSaidaNaoContribuinteInterestadual(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="baseCalculoSt">
                            {t('pages.products.fiscal.baseCalculationSt')}
                        </label>
                        <input
                            id="baseCalculoSt"
                            type="number"
                            step="0.0001"
                            value={baseCalculoSt}
                            onChange={(e) => setBaseCalculoSt(e.target.value)}
                            disabled={formLoading || !can.edit}
                            className={styles.input}
                            placeholder={t('pages.products.fiscal.baseCalculationStPlaceholder')}
                        />
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="ipiSaida">
                            {t('pages.products.fiscal.ipiOutput')}
                        </label>
                        <input
                            id="ipiSaida"
                            type="number"
                            step="0.0001"
                            value={ipiSaida}
                            onChange={(e) => setIpiSaida(e.target.value)}
                            disabled={formLoading || !can.edit}
                            className={styles.input}
                            placeholder={t('pages.products.fiscal.ipiOutputPlaceholder')}
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaInterestadual">
                            {t('pages.products.fiscal.cfopInterstate')}
                        </label>
                        <select
                            id="cfopSaidaInterestadual"
                            value={cfopSaidaInterestadual}
                            onChange={(e) => setCfopSaidaInterestadual(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaBrindeBonificacaoDoacaoInterestadual">
                            {t('pages.products.fiscal.cfopGiftInterstate')}
                        </label>
                        <select
                            id="cfopSaidaBrindeBonificacaoDoacaoInterestadual"
                            value={cfopSaidaBrindeBonificacaoDoacaoInterestadual}
                            onChange={(e) => setCfopSaidaBrindeBonificacaoDoacaoInterestadual(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopSaidaNaoContribuinteInterestadualOutro">
                            {t('pages.products.fiscal.cfopNonContributorInterstateOther')}
                        </label>
                        <select
                            id="cfopSaidaNaoContribuinteInterestadualOutro"
                            value={cfopSaidaNaoContribuinteInterestadualOutro}
                            onChange={(e) => setCfopSaidaNaoContribuinteInterestadualOutro(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopBonificacaoExportacao">
                            {t('pages.products.fiscal.cfopBonusExport')}
                        </label>
                        <select
                            id="cfopBonificacaoExportacao"
                            value={cfopBonificacaoExportacao}
                            onChange={(e) => setCfopBonificacaoExportacao(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="cfopBonificacaoExportacaoOutro">
                            {t('pages.products.fiscal.cfopBonusExportOther')}
                        </label>
                        <select
                            id="cfopBonificacaoExportacaoOutro"
                            value={cfopBonificacaoExportacaoOutro}
                            onChange={(e) => setCfopBonificacaoExportacaoOutro(e.target.value)}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                            className={styles.select}
                        >
                            <option value="">{t('common.select')}</option>
                            {allCfops.map(cfop => (
                                <option key={cfop.id} value={cfop.id}>
                                    {cfop.codigoOperacao} - {cfop.descricao}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    };

    // Modal de Importacao de MPs
    const renderImportMPModal = () => {
        if (!showImportMPModal) return null;

        const filteredImportProducts = (products || []).filter(p =>
            p.id !== editingItem?.id &&
            p.rawMaterials &&
            p.rawMaterials.length > 0 &&
            (
                !importSearchTerm ||
                p.commercialName?.toLowerCase().includes(importSearchTerm.toLowerCase()) ||
                p.code?.toLowerCase().includes(importSearchTerm.toLowerCase())
            )
        );

        return (
            <div
                className={styles.importModalOverlay}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowImportMPModal(false);
                        setImportSearchTerm('');
                    }
                }}
            >
                <div className={styles.importModalContainer}>
                    <div className={styles.importModalHeader}>
                        <div>
                            <h2><FiDownload size={20} style={{ marginRight: '10px' }} />{t('pages.products.importModal.title')}</h2>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowImportMPModal(false);
                                setImportSearchTerm('');
                            }}
                            className={styles.importModalCloseButton}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    <div className={styles.importModalContent}>
                        <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                            <label htmlFor="importSearch">{t('pages.products.importModal.search')}</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    id="importSearch"
                                    type="text"
                                    placeholder={t('pages.products.importModal.searchPlaceholder')}
                                    value={importSearchTerm}
                                    onChange={(e) => setImportSearchTerm(e.target.value)}
                                    className={styles.input}
                                    autoFocus
                                    disabled={!can.edit}
                                    style={{ paddingLeft: '36px' }}
                                />
                                <FiSearch size={18} style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'var(--text-secondary)'
                                }} />
                            </div>
                        </div>

                        <div className={styles.tableContainer} style={{ maxHeight: '400px', overflow: 'auto' }}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.products.importModal.code')}</th>
                                        <th>{t('pages.products.importModal.commercialName')}</th>
                                        <th style={{ width: '100px', textAlign: 'center' }}>{t('pages.products.importModal.quantity')}</th>
                                        <th style={{ width: '120px' }}>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredImportProducts.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className={styles.emptyTable}>
                                                {importSearchTerm ? t('pages.products.importModal.noProductsSearch') : t('pages.products.importModal.noProducts')}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredImportProducts.map(product => (
                                            <tr key={product.id}>
                                                <td><strong>{product.code}</strong></td>
                                                <td>{product.commercialName}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={{
                                                        backgroundColor: 'var(--primary-color)',
                                                        color: 'white',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        {product.rawMaterials.length}
                                                    </span>
                                                </td>
                                                <td>
                                                    {can.edit ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleImportMPs(product)}
                                                            className={styles.importActionButton}
                                                        >
                                                            <FiDownload size={14} style={{ marginRight: '4px' }} />
                                                            {t('pages.products.importModal.import')}
                                                        </button>
                                                    ) : (
                                                        <span className={styles.textMuted}>{t('pages.products.form.noActions')}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.importModalActions}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowImportMPModal(false);
                                    setImportSearchTerm('');
                                }}
                                className={styles.importCancelButton}
                            >
                                <FiX size={18} /> {t('common.close')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // renderRawMaterialsTable
    const renderRawMaterialsTable = () => {
        const availableOptions = rawMaterialOptions;

        const unitCategoryIds = allCategories
            .filter(c => {
                const name = c.name?.toUpperCase();
                return name === 'SERIGRAFIA' || name === 'EMBALAGEM';
            })
            .map(c => c.id);

        const isUnitCalculation = (mpId) => {
            const mpData = allRawMaterials.find(item => item.id === mpId);
            return mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
        };

        const calculateLineTotal = (mpId, valorAtual, quantity) => {
            const v = parseFloat(valorAtual) || 0;
            const q = parseFloat(quantity) || 0;
            if (isUnitCalculation(mpId)) {
                return v * q;
            } else {
                return (v * q) / 100;
            }
        };

        const rawMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                const currentPrice = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (currentPrice * quantity) / 100;
            }
            return acc;
        }, 0);

        const rawMaterialsTotalCost = rawMaterialsCost * (parseFloat(netWeight) || 0);
        const unitMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
            if (isUnitCalculation(mp.id)) {
                const currentPrice = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (currentPrice * quantity);
            }
            return acc;
        }, 0);

        const totalCost = rawMaterialsTotalCost + unitMaterialsCost;
        const totalPercentageQuantity = rawMaterialsList.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                return acc + (parseFloat(mp.quantity) || 0);
            }
            return acc;
        }, 0);

        const hasPercentageMaterials = rawMaterialsList.some(mp => !isUnitCalculation(mp.id));
        const isQuantityValid = !hasPercentageMaterials || Math.abs(totalPercentageQuantity - 100) <= 0.01;
        const finalClientValue = totalCost * (parseFloat(markup) || 1);

        return (
            <>
                <div style={{ display: 'flex', gap: '20px', width: '100%', marginBottom: '15px' }}>
                    <div className={styles.inputGroup} style={{ flex: 1 }}>
                        <label htmlFor="filterType">{t('pages.products.form.filterByType')}</label>
                        <select
                            id="filterType"
                            value={filterProductTypeId}
                            onChange={(e) => setFilterProductTypeId(e.target.value)}
                            className={`${styles.input} ${styles.select}`}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                        >
                            <option value="">{t('pages.products.form.allTypes')}</option>
                            {allProductTypes && allProductTypes.map(type => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup} style={{ flex: 1 }}>
                        <label htmlFor="filterCategory">{t('pages.products.form.filterByCategory')}</label>
                        <select
                            id="filterCategory"
                            value={filterCategoryId}
                            onChange={(e) => setFilterCategoryId(e.target.value)}
                            className={`${styles.input} ${styles.select}`}
                            disabled={formLoading || auxiliaryDataLoading || !can.edit}
                        >
                            <option value="">{t('pages.products.form.allCategories')}</option>
                            {allCategories && allCategories.map(category => (
                                <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                    <label htmlFor="addRawMaterial">{t('pages.products.form.addRawMaterial')}</label>
                    <Select
                        id="addRawMaterial"
                        onChange={handleAddRawMaterial}
                        options={availableOptions}
                        classNamePrefix="react-select"
                        placeholder={
                            auxiliaryDataLoading ? t('common.loading') :
                                (availableOptions.length === 0) ? t('pages.products.form.noOptions') : t('pages.products.form.searchPlaceholder')
                        }
                        isDisabled={formLoading || auxiliaryDataLoading || availableOptions.length === 0 || !can.edit}
                        value={null}
                        isSearchable={true}
                        filterOption={(option, inputValue) => {
                            const searchTerm = inputValue.toLowerCase().trim();
                            if (!searchTerm) return true;
                            const label = option.label.toLowerCase();
                            if (label.includes(searchTerm)) return true;
                            const searchData = option.data?.searchData;
                            if (searchData) {
                                if (searchData.commercialName?.toLowerCase().includes(searchTerm)) return true;
                                if (searchData.inciName?.toLowerCase().includes(searchTerm)) return true;
                                if (searchData.code?.toLowerCase().includes(searchTerm)) return true;
                            }
                            return false;
                        }}
                    />
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '80px', textAlign: 'center' }}>{t('pages.products.form.order')}</th>
                                <th style={{ width: '80px' }}>{t('pages.products.form.phase')}</th>
                                <th>{t('pages.products.form.commercialNameCol')}</th>
                                <th style={{ width: '120px' }}>{t('pages.products.form.cost')}</th>
                                <th style={{ width: '100px' }}>{t('pages.products.form.quantity')}</th>
                                <th style={{ width: '120px' }}>{t('pages.products.form.total')}</th>
                                <th style={{ width: '100px' }}>{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rawMaterialsList.length === 0 ? (
                                <tr><td colSpan="7" className={styles.emptyTable}>{t('pages.products.form.noMaterials')}</td></tr>
                            ) : (
                                <>
                                    {rawMaterialsList.map((mp, index) => {
                                        const currentPrice = getLatestPrice(mp.id, mp.valor);
                                        const lineTotal = calculateLineTotal(mp.id, currentPrice, mp.quantity);
                                        const isUnit = isUnitCalculation(mp.id);

                                        return (
                                            <tr key={mp.rowId}>
                                                <td>
                                                    <div className={styles.orderControls}>
                                                        <button type="button" className={styles.orderButton} onClick={() => handleMoveRawMaterial(index, 'up')} disabled={index === 0 || formLoading || !can.edit}>▲</button>
                                                        <span style={{ fontWeight: 'bold', margin: '0 4px' }}>{index + 1}</span>
                                                        <button type="button" className={styles.orderButton} onClick={() => handleMoveRawMaterial(index, 'down')} disabled={index === rawMaterialsList.length - 1 || formLoading || !can.edit}>▼</button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={mp.fase}
                                                        onChange={(e) => handleUpdateRawMaterialField(mp.rowId, 'fase', e.target.value)}
                                                        className={styles.input}
                                                        disabled={formLoading || !can.edit}
                                                        style={{ width: '50px' }}
                                                    />
                                                </td>
                                                <td>{mp.inciName}</td>
                                                <td style={{ color: 'var(--primary-color)', fontWeight: '500' }}>
                                                    {currentPrice.toFixed(5)}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <input
                                                            type="number"
                                                            step="0.0001"
                                                            value={mp.quantity}
                                                            onChange={(e) => handleUpdateRawMaterialField(mp.rowId, 'quantity', e.target.value)}
                                                            className={styles.input}
                                                            disabled={formLoading || !can.edit}
                                                            style={{ width: '80px' }}
                                                        />
                                                        <span style={{ fontSize: '0.8rem', color: '#666' }}>
                                                            {isUnit ? 'un' : '%'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                                                    {lineTotal.toFixed(8)}
                                                </td>
                                                <td>
                                                    {can.edit ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveRawMaterial(mp.rowId)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}>
                                                            <FiTrash2 size={14} />
                                                        </button>
                                                    ) : (
                                                        <span className={styles.textMuted}>-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    <tr className={styles.tableFooterRow} style={{ backgroundColor: isQuantityValid ? '#f1f8e9' : '#fff8e1' }}>
                                        <td colSpan="4" className={styles.labelRight}>
                                            <strong>{t('pages.products.form.totalMaterials')}</strong>
                                        </td>
                                        <td className={styles.valueCell} style={{
                                            color: isQuantityValid ? '#2e7d32' : '#f44336',
                                            fontWeight: 'bold'
                                        }}>
                                            {totalPercentageQuantity.toFixed(2)}%
                                        </td>
                                        <td colSpan="2"></td>
                                    </tr>

                                    {!isQuantityValid && (
                                        <tr className={styles.tableFooterRow} style={{ backgroundColor: '#fff8e1' }}>
                                            <td colSpan="7" style={{ color: '#f44336', textAlign: 'center', padding: '8px', fontSize: '0.85rem' }}>
                                                <FaExclamationTriangle size={14} style={{ marginRight: '6px' }} />
                                                {t('pages.products.form.warningPercentage', { total: totalPercentageQuantity.toFixed(2) })}
                                            </td>
                                        </tr>
                                    )}

                                    <tr className={styles.tableFooterRow}>
                                        <td colSpan="5" className={styles.labelRight}>
                                            <strong>{t('pages.products.form.totalFormulationCost')}</strong>
                                        </td>
                                        <td className={styles.valueCell}>
                                            {totalCost.toLocaleString('pt-BR', { minimumFractionDigits: 5 })}
                                        </td>
                                        <td></td>
                                    </tr>

                                    <tr className={styles.tableFooterRow}>
                                        <td colSpan="5" className={styles.labelRight}>{t('pages.products.form.markup')}</td>
                                        <td className={styles.valueCell}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={markup}
                                                onChange={(e) => setMarkup(e.target.value)}
                                                className={styles.markupInputClean}
                                                disabled={!can.edit}
                                            />
                                        </td>
                                        <td></td>
                                    </tr>

                                    <tr className={`${styles.tableFooterRow} ${styles.totalHighlight}`}>
                                        <td colSpan="5" className={styles.labelRight} style={{ color: 'inherit' }}>
                                            <strong>{t('pages.products.form.suggestedPrice')}</strong>
                                        </td>
                                        <td className={styles.valueCell} style={{ color: 'inherit', fontSize: '1.1rem' }}>
                                            {finalClientValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                                        </td>
                                        <td></td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    const renderPreparationSteps = () => {
        const activeSetups = preparationSetups.filter(setup =>
            setup.situation === 'Ativo' && setup.preparationSteps?.length > 0
        );

        return (
            <>
                <h3 className={styles.subtitle}>{t('pages.products.form.preparationSteps')}</h3>

                {activeSetups.length > 0 && (
                    <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                        <label htmlFor="setupSelector">{t('pages.products.form.loadSetup')}</label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <select
                                id="setupSelector"
                                value={selectedSetup}
                                onChange={(e) => handleLoadSetup(e.target.value)}
                                className={styles.input}
                                style={{ flex: 1 }}
                                disabled={formLoading || !can.edit}
                            >
                                <option value="">{t('pages.products.form.selectSetup')}</option>
                                {activeSetups.map(setup => (
                                    <option key={setup.id} value={setup.id}>
                                        {setup.name} ({setup.preparationSteps.length} {t('pages.products.form.steps')})
                                    </option>
                                ))}
                            </select>
                            {selectedSetup && can.edit && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedSetup('');
                                        setPreparationSteps([]);
                                    }}
                                    className={styles.cancelButton}
                                    style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}
                                >
                                    <FiX size={14} style={{ marginRight: '4px' }} /> {t('pages.products.form.clearSetup')}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                    <label htmlFor="preparationStep">{t('pages.products.form.addStepManually')}</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            id="preparationStep"
                            type="text"
                            value={currentStep}
                            onChange={(e) => setCurrentStep(e.target.value)}
                            onKeyPress={handleStepKeyPress}
                            className={styles.input}
                            placeholder={t('pages.products.form.stepPlaceholder')}
                            disabled={formLoading || !can.edit}
                            style={{ flex: 1 }}
                        />
                        {can.edit && (
                            <button
                                type="button"
                                onClick={handleAddPreparationStep}
                                className={styles.addButton}
                                disabled={formLoading || !currentStep.trim()}
                                style={{
                                    padding: '8px 16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <FiPlus size={16} style={{ marginRight: '4px' }} /> {t('pages.products.form.addStep')}
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '80px', textAlign: 'center' }}>{t('pages.products.form.order')}</th>
                                <th>{t('pages.products.form.stepDescription')}</th>
                                <th style={{ width: '100px' }}>{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preparationSteps.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className={styles.emptyTable}>
                                        {activeSetups.length > 0 ? t('pages.products.form.noStepsWithSetup') : t('pages.products.form.noSteps')}
                                    </td>
                                </tr>
                            ) : (
                                preparationSteps.map((step, index) => (
                                    <tr key={step.id}>
                                        <td>
                                            <div className={styles.orderControls}>
                                                <button
                                                    type="button"
                                                    className={styles.orderButton}
                                                    onClick={() => handleMovePreparationStep(index, 'up')}
                                                    disabled={index === 0 || formLoading || !can.edit}
                                                >
                                                    ▲
                                                </button>
                                                <span style={{ fontWeight: 'bold', margin: '0 4px' }}>
                                                    {index + 1}
                                                </span>
                                                <button
                                                    type="button"
                                                    className={styles.orderButton}
                                                    onClick={() => handleMovePreparationStep(index, 'down')}
                                                    disabled={index === preparationSteps.length - 1 || formLoading || !can.edit}
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={step.description}
                                                onChange={(e) => handleUpdatePreparationStep(step.id, e.target.value)}
                                                className={styles.input}
                                                disabled={formLoading || !can.edit}
                                                style={{ width: '100%' }}
                                            />
                                        </td>
                                        <td>
                                            {can.edit ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePreparationStep(step.id)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    disabled={formLoading}
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            ) : (
                                                <span className={styles.textMuted}>-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {preparationSteps.length > 0 && (
                    <div style={{
                        marginTop: '10px',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>{t('pages.products.form.totalSteps', { count: preparationSteps.length })}</span>
                        {selectedSetup && (
                            <span style={{ color: 'var(--primary-color)', fontWeight: '500' }}>
                                {t('pages.products.form.setup')}: {preparationSetups.find(s => s.id === selectedSetup)?.name}
                            </span>
                        )}
                    </div>
                )}
            </>
        );
    };

    const renderQualityCriteriaTable = () => {
        const availableCriteria = qualityCriteriaOptions.filter(opt => !qualityCriteria.find(qc => qc.id === opt.value));
        return (
            <>
                <h3 className={styles.subtitle}>{t('pages.products.form.qualityCriteria')}</h3>
                <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                    <label htmlFor="addCriteria">{t('pages.products.form.addCriteria')}</label>
                    <select id="addCriteria" onChange={handleAddCriteria} className={`${styles.input} ${styles.select}`} disabled={formLoading || auxiliaryDataLoading || availableCriteria.length === 0 || !can.edit} value={''}>
                        <option value="" disabled>{auxiliaryDataLoading ? t('common.loading') : (availableCriteria.length === 0) ? t('pages.products.form.noCriteria') : t('pages.products.form.selectCriteria')}</option>
                        {availableCriteria.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '80px', textAlign: 'center' }}>{t('pages.products.form.order')}</th>
                                <th>{t('pages.products.form.analysis')}</th>
                                <th style={{ width: '250px' }}>{t('pages.products.form.specification')}</th>
                                <th>{t('pages.products.form.method')}</th>
                                <th style={{ width: '120px' }}>{t('pages.products.form.control')}</th>
                                <th style={{ width: '100px' }}>{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {qualityCriteria.length === 0 ? (
                                <tr><td colSpan="6" className={styles.emptyTable}>{t('pages.products.form.noCriteriaAdded')}</td></tr>
                            ) : (
                                qualityCriteria.map((c, index) => (
                                    <tr key={c.id}>
                                        <td>
                                            <div className={styles.orderControls}>
                                                <button type="button" className={styles.orderButton} onClick={() => handleMoveCriteria(index, 'up')} disabled={index === 0 || formLoading || !can.edit}>▲</button>
                                                <span style={{ margin: '0 4px' }}>{index + 1}</span>
                                                <button type="button" className={styles.orderButton} onClick={() => handleMoveCriteria(index, 'down')} disabled={index === qualityCriteria.length - 1 || formLoading || !can.edit}>▼</button>
                                            </div>
                                        </td>
                                        <td>{c.analysis}</td>
                                        <td><input type="text" value={c.specification} onChange={(e) => handleUpdateCriteriaField(c.id, 'specification', e.target.value)} className={styles.input} disabled={formLoading || !can.edit} /></td>
                                        <td>{c.method}</td>
                                        <td>
                                            <select value={c.control} onChange={(e) => handleUpdateCriteriaField(c.id, 'control', e.target.value)} className={`${styles.input} ${styles.select}`} disabled={formLoading || !can.edit}>
                                                {ANALISIS_CONTROL_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </td>
                                        <td>
                                            {can.edit ? (
                                                <button type="button" onClick={() => handleRemoveCriteria(c.id)} className={`${styles.actionButton} ${styles.deleteButton}`} disabled={formLoading}>
                                                    <FiTrash2 size={14} />
                                                </button>
                                            ) : (
                                                <span className={styles.textMuted}>-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    // --- Table Render ---
    const renderTable = () => {
        const showSkeleton = mainLoading && products.length === 0;

        if (showSkeleton) {
            return <TableSkeleton columns={6} rows={ITEMS_PER_PAGE} />;
        }

        if (currentProducts.length === 0) {
            return (
                <EmptyState
                    icon="📦"
                    title={mainSearchTerm ? t('pages.products.table.emptySearch') : t('pages.products.table.empty')}
                    message={mainSearchTerm ? t('pages.products.search.noResults') : t('pages.products.table.emptyMessage')}
                    actionText={can.create ? t('pages.products.newProduct') : undefined}
                    onAction={can.create ? () => { resetForm(); setIsModalOpen(true); } : undefined}
                />
            );
        }

        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>{t('pages.products.table.code')}</th>
                        <th>{t('pages.products.table.commercialName')}</th>
                        <th>{t('pages.products.table.barcode')}</th>
                        <th>{t('pages.products.table.type')}</th>
                        <th>{t('pages.products.table.category')}</th>
                        <th style={{ textAlign: 'right' }}>{t('pages.products.table.suggestedPrice')}</th>
                        <th style={{ width: '200px' }}>{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentProducts.map(item => {
                        const sugestaoPreco = calculateSuggestedPrice(item);
                        return (
                            <tr key={item.id} className={styles.tableRow}>
                                <td><strong>{item.code}</strong></td>
                                <td>{item.commercialName}</td>
                                <td>{item.barcode || '-'}</td>
                                <td>{item.productTypeIds?.map(id => getParamName(id, productTypes)).join(', ') || 'N/A'}</td>
                                <td>{item.categoryIds?.map(id => getParamName(id, categories)).join(', ') || 'N/A'}</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                    {sugestaoPreco.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 5
                                    })}
                                </td>
                                <td>
                                    <div className={styles.actionsContainer}>
                                        {can.edit && (
                                            <button onClick={() => handleEditClick(item)} className={`${styles.actionButton} ${styles.editButton}`} title={t('common.edit')}>
                                                <FiEdit size={16} />
                                            </button>
                                        )}
                                        {can.delete && (
                                            <button onClick={() => handleDeleteClick(item.id)} className={`${styles.actionButton} ${styles.deleteButton}`} title={t('common.delete')}>
                                                <FiTrash2 size={16} />
                                            </button>
                                        )}
                                        {!can.edit && !can.delete && (
                                            <span className={styles.textMuted}>{t('common.noActionsAvailable')}</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    // --- Modal Content ---
    const renderModalContent = () => {
        const recommendation = getCodeRecommendation();
        const isFormDisabled = !can.create && !can.edit;

        const productTypeOptions = allProductTypes?.map(t => ({ id: t.id, name: t.name })) || [];
        const categoryOptions = allCategories?.map(c => ({ id: c.id, name: c.name })) || [];

        return (
            <div className={styles.formContent}>
                {/* Section 1: Basic Information */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.products.form.basicInformation')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="sigla">
                                {t('pages.products.form.acronym')} <span className={styles.required}>*</span>
                            </label>
                            <input
                                id="sigla"
                                type="text"
                                value={sigla}
                                onChange={handleSiglaChange}
                                required
                                disabled={formLoading || !!editingItem || isFormDisabled}
                                className={styles.input}
                                maxLength={5}
                                placeholder={t('pages.products.form.acronymPlaceholder')}
                            />
                            <small className={styles.fieldHint}>{t('pages.products.form.acronymHint')}</small>
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="code">
                                {t('pages.products.form.code')} <span className={styles.required}>*</span>
                            </label>
                            <input
                                id="code"
                                type="text"
                                value={code}
                                onChange={handleCodeChange}
                                className={styles.input}
                                disabled={formLoading || !sigla || isFormDisabled}
                                placeholder={sigla ? `${sigla}001` : t('pages.products.form.codePlaceholder')}
                            />
                            {recommendation && !editingItem && (
                                <small className={styles.suggestionHint}>
                                    {t('pages.products.form.codeHint')} <strong>{recommendation}</strong> {t('pages.products.form.codeHintSuffix')}
                                </small>
                            )}
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="commercialName">
                                {t('pages.products.form.commercialName')} <span className={styles.required}>*</span>
                            </label>
                            <input
                                id="commercialName"
                                type="text"
                                value={commercialName}
                                onChange={(e) => setCommercialName(e.target.value)}
                                required
                                disabled={formLoading || isFormDisabled}
                                className={styles.input}
                                placeholder={t('pages.products.form.commercialNamePlaceholder')}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="barcode">
                                {t('pages.products.form.barcode')}
                            </label>
                            <input
                                id="barcode"
                                type="text"
                                value={barcode}
                                onChange={(e) => setBarcode(e.target.value)}
                                disabled={formLoading || isFormDisabled}
                                className={styles.input}
                                placeholder={t('pages.products.form.barcodePlaceholder')}
                            />
                            <small className={styles.fieldHint}>{t('pages.products.form.barcodeHint')}</small>
                        </div>
                    </div>
                </div>

                {/* Section 2: Physical Characteristics */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaBalanceScale size={18} /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.products.form.physicalCharacteristics')}</h3>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="situation">{t('pages.products.form.situation')}</label>
                            <select
                                id="situation"
                                value={situation}
                                onChange={(e) => setSituation(e.target.value)}
                                disabled={formLoading || isFormDisabled}
                                className={styles.select}
                            >
                                {SITUATION_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="netWeight">{t('pages.products.form.netWeight')}</label>
                            <input
                                id="netWeight"
                                type="text"
                                inputMode="decimal"
                                value={netWeight}
                                onChange={(e) => setNetWeight(e.target.value)}
                                disabled={formLoading || isFormDisabled}
                                className={styles.input}
                                placeholder={t('pages.products.form.netWeightPlaceholder')}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="grossWeight">{t('pages.products.form.grossWeight')}</label>
                            <input
                                id="grossWeight"
                                type="number"
                                step="0.01"
                                value={grossWeight}
                                onChange={(e) => setGrossWeight(e.target.value)}
                                disabled={formLoading || isFormDisabled}
                                className={styles.input}
                                placeholder={t('pages.products.form.grossWeightPlaceholder')}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="density">{t('pages.products.form.density')}</label>
                            <input
                                id="density"
                                type="number"
                                step="0.01"
                                value={density}
                                onChange={(e) => setDensity(e.target.value)}
                                disabled={formLoading || isFormDisabled}
                                className={styles.input}
                                placeholder={t('pages.products.form.densityPlaceholder')}
                            />
                        </div>
                    </div>
                </div>

                {/* Section 3: Classification - Using TagInput */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaTags size={18} /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.products.form.classification')}</h3>
                    </div>
                    <div className={styles.classificationGrid}>
                        <TagInput
                            label={t('pages.products.form.productTypes')}
                            options={productTypeOptions}
                            selectedIds={selectedProductTypeIds}
                            onAdd={handleAddProductType}
                            onRemove={handleRemoveProductType}
                            placeholder={t('pages.products.form.addProductType')}
                            disabled={formLoading || isFormDisabled}
                            getOptionLabel={(item) => item.name}
                            getOptionId={(item) => item.id}
                        />
                        <TagInput
                            label={t('pages.products.form.categories')}
                            options={categoryOptions}
                            selectedIds={selectedCategoryIds}
                            onAdd={handleAddCategory}
                            onRemove={handleRemoveCategory}
                            placeholder={t('pages.products.form.addCategory')}
                            disabled={formLoading || isFormDisabled}
                            getOptionLabel={(item) => item.name}
                            getOptionId={(item) => item.id}
                        />
                    </div>
                </div>

                {/* Tabs Section */}
                <div className={styles.tabsContainer}>
                    <div className={styles.tabsHeader}>
                        {Object.values(TABS).map(tab => {
                            const tabKey = tab === TABS.MATERIAIS_E_PROC ? 'materials' :
                                tab === TABS.FISCAL ? 'fiscal' :
                                    tab === TABS.ESPECIFICACAO_TECNICA ? 'technicalSpec' : 'technical';
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    className={`${styles.tabButton} ${activeTab === tab ? styles.activeTab : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                    disabled={formLoading}
                                >
                                    {tab === TABS.MATERIAIS_E_PROC && <SlChemistry size={14} style={{ marginRight: '6px' }} />}
                                    {tab === TABS.FISCAL && <FiFileText size={14} style={{ marginRight: '6px' }} />}
                                    {tab === TABS.ESPECIFICACAO_TECNICA && <FiClipboard size={14} style={{ marginRight: '6px' }} />}
                                    {tab === TABS.TECNICO && <FaCogs size={14} style={{ marginRight: '6px' }} />}
                                    {t(`pages.products.tabs.${tabKey}`)}
                                </button>
                            );
                        })}
                    </div>
                    <div className={styles.tabsContent}>
                        {activeTab === TABS.MATERIAIS_E_PROC && (
                            <div className={styles.tabSection}>
                                <div className={styles.subSection}>
                                    <div className={styles.subSectionHeader}>
                                        <div className={styles.subSectionIcon}><FaMicroscope size={18} /></div>
                                        <h4 className={styles.subSectionTitle}>{t('pages.products.form.rawMaterials')}</h4>
                                        <div className={styles.subSectionActions}>
                                            {rawMaterialsList.length > 0 && can.view && (
                                                <button
                                                    type="button"
                                                    onClick={handleExportRawMaterials}
                                                    className={styles.iconButton}
                                                    title={t('pages.products.form.exportList')}
                                                >
                                                    <FiDownload size={16} />
                                                </button>
                                            )}
                                            {can.edit && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowImportMPModal(true)}
                                                    className={styles.iconButton}
                                                    title={t('pages.products.form.importFromProduct')}
                                                >
                                                    <FiUpload size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {renderRawMaterialsTable()}
                                </div>

                                <div className={styles.subSection}>
                                    <div className={styles.subSectionHeader}>
                                        <div className={styles.subSectionIcon}><FaCogs size={18} /></div>
                                        <h4 className={styles.subSectionTitle}>{t('pages.products.form.preparationSteps')}</h4>
                                    </div>
                                    {renderPreparationSteps()}
                                </div>

                                <div className={styles.subSection}>
                                    <div className={styles.subSectionHeader}>
                                        <div className={styles.subSectionIcon}><FiCheck size={18} /></div>
                                        <h4 className={styles.subSectionTitle}>{t('pages.products.form.qualityCriteria')}</h4>
                                    </div>
                                    {renderQualityCriteriaTable()}
                                </div>
                            </div>
                        )}

                        {activeTab === TABS.TECNICO && renderTechnicianTab()}
                        {activeTab === TABS.FISCAL && renderFiscalTab()}

                        {activeTab === TABS.ESPECIFICACAO_TECNICA && (
                            <div className={styles.comingSoonTab}>
                                <div className={styles.comingSoonIcon}><FiFileText size={48} /></div>
                                <h4>{t('pages.products.technicalSpec.comingSoon')}</h4>
                                <p>{t('pages.products.technicalSpec.comingSoonMessage')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.pageContainer}>
            {/* Toast System */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            )}

            {/* Product Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={resetForm}
                onSubmit={handleSave}
                title={editingItem ? t('pages.products.editProduct') : t('pages.products.newProduct')}
                subtitle={editingItem ? `${t('pages.products.editingLabel')}: ${editingItem.commercialName} (${editingItem.code})` : undefined}
                isLoading={formLoading}
                error={formStatus.type === 'error' ? formStatus.message : ''}
                success={formStatus.type === 'success' ? formStatus.message : ''}
                submitText={editingItem ? t('common.saveChanges') : t('common.create')}
                maxWidth="1000px"
            >
                {renderModalContent()}
            </FormModal>

            {/* Import MP Modal */}
            {renderImportMPModal()}

            {/* Page Header */}
            <PageHeader
                title={t('pages.products.title')}
                subtitle={t('pages.products.subtitle')}
                actionText={can.create ? t('pages.products.newProduct') : undefined}
                onAction={() => { resetForm(); setIsModalOpen(true); }}
                disabled={mainLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div className={styles.inputGroup} style={{ flex: '1' }}>
                        <label>{t('pages.products.search.placeholder')}</label>
                        <SearchBar
                            placeholder={t('pages.products.search.placeholder')}
                            onSearch={setMainSearchTerm}
                            disabled={mainLoading}
                            debounceDelay={300}
                            initialValue={mainSearchTerm}
                        />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                        <label>{t('pages.products.filters.type')}</label>
                        <select
                            value={mainFilterProductTypeId}
                            onChange={(e) => handleMainFilterChange(setMainFilterProductTypeId, e.target.value)}
                            className={styles.input}
                            disabled={mainLoading}
                        >
                            <option value="">{t('common.all')}</option>
                            {allProductTypes && allProductTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                        <label>{t('pages.products.filters.category')}</label>
                        <select
                            value={mainFilterCategoryId}
                            onChange={(e) => handleMainFilterChange(setMainFilterCategoryId, e.target.value)}
                            className={styles.input}
                            disabled={mainLoading}
                        >
                            <option value="">{t('common.all')}</option>
                            {allCategories && allCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                {renderTable()}
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredProducts.length}
                onPageChange={goToPage}
                disabled={mainLoading}
                itemsLabel={t('pages.products.table.items')}
            />
        </div>
    );
};

export default ProductsPage;